import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminSidebar from '../../Sidebar/AdminSidebar';
import AdminNavbar from '../../Navbars/AdminNavbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../../apis/superAdminApi';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { Oval } from 'react-loader-spinner';
import {
    AlertTriangle, Download, FileText, Search, Ban, Eye, X, Info, RefreshCw, ChevronDown, Pencil,
    CornerUpLeft, Truck, Upload, Trash2} from 'lucide-react';

const SYMBOLS: any = { USD: '$', NGN: '₦', GBP: '£', EUR: '€' };

/**
 * Amounts render in the currency of the thing being shown — the preview uses the
 * client's current currency, a generated invoice uses the one it was raised in.
 */
const formatMoney = (amount: any, symbol = '$') => {
    const num = Number(amount);
    const body = isNaN(num) ? '0.00' : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${symbol}${body}`;
};

const symbolFor = (currency?: string) => SYMBOLS[currency || ''] || '$';

const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
};

const statusChip = (status: string) => {
    switch (status) {
        case 'paid': return 'bg-green-100 text-green-700 border-green-200';
        case 'sent': return 'bg-blue-100 text-blue-700 border-blue-200';
        case 'void': return 'bg-gray-200 text-gray-600 border-gray-300';
        // A draft is an interrupted run, not a bill. Coloured like a warning because
        // it is holding a lock on shipments without having charged for them.
        case 'draft': return 'bg-red-100 text-red-700 border-red-300';
        default: return 'bg-amber-100 text-amber-700 border-amber-200';
    }
};

/** Trigger a browser download from a blob returned by the API. */
const downloadBlob = (blob: any, filename: string) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * The message the API actually sent.
 *
 * The shared axios interceptor has no case for 422, so a validation failure rejects
 * and lands in a catch block — where the real reason used to be thrown away and
 * replaced with a generic "failed". Laravel puts the useful detail in `errors`
 * (per-field) and a summary in `message`, so prefer the former.
 */
const apiError = (e: any, fallback: string): string => {
    const data = e?.response?.data;

    const fieldErrors = data?.errors
        ? Object.values(data.errors).flat().filter(Boolean)
        : [];

    if (fieldErrors.length) return fieldErrors.join(' ');

    return data?.message || e?.message || fallback;
};

const firstOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

function ClientInvoices() {
    // Deleting an invoice is administrator-only — the server is the authority on
    // that, this just keeps the button off screens that would only be refused.
    const userLoginData = useSelector((state: any) => state.data.login.value);
    const isAdmin = ['admin', 'super_admin'].includes(userLoginData?.data?.role);

    const [clients, setClients] = useState<any[]>([]);

    // Which of the two billing streams this page is driving. Last mile prices what
    // the system already knows; returns are priced from a sheet the client sends,
    // because nothing here records what a return cost in customs.
    const [billMode, setBillMode] = useState<'lastmile' | 'return'>('lastmile');

    // The parsed return sheet, and what the server made of it.
    const [returnRows, setReturnRows] = useState<any[]>([]);
    const [returnFile, setReturnFile] = useState<string>('');
    const [returnColumns, setReturnColumns] = useState<any>(null);
    const [returnPreview, setReturnPreview] = useState<any>(null);
    const [returnBusy, setReturnBusy] = useState<string>('');

    // The trading name the invoice is issued UNDER. Blank means Skynet, which is what
    // every invoice raised before this feature existed used.
    const [companies, setCompanies] = useState<any[]>([]);
    const [companyId, setCompanyId] = useState<string>('');

    // Additional clients billed onto the same invoice. Kept separate from clientId so
    // the docket picker, weight overrides and invoice list stay driven by one primary.
    // Chosen clients, in the order they were picked. The first is what the invoice
    // header and the docket list key on, but that is an implementation detail — there
    // is no "primary" to choose.
    const [chosen, setChosen] = useState<any[]>([]);
    const clientId = chosen[0] ?? '';
    const alsoBill = chosen.slice(1);

    // Whether a docket number is required to bill a shipment. Asked before every
    // preview rather than assumed: it changes what is billed and by how much.
    const [requireDocket, setRequireDocket] = useState<boolean>(true);
    const [askDocket, setAskDocket] = useState<boolean>(false);

    const [clientQuery, setClientQuery] = useState<string>('');
    const [clientOpen, setClientOpen] = useState<boolean>(false);
    const clientPickerRef = useRef<HTMLDivElement>(null);
    const [from, setFrom] = useState<string>(firstOfMonth());
    const [to, setTo] = useState<string>(new Date().toISOString().slice(0, 10));
    const [deliveredOnly, setDeliveredOnly] = useState<boolean>(false);
    const [excludeReturns, setExcludeReturns] = useState<boolean>(true);

    // Docket selection. Picking one bills that whole docket and the dates step aside.
    const [docket, setDocket] = useState<string>('');
    const [dockets, setDockets] = useState<any[]>([]);
    const [docketQuery, setDocketQuery] = useState<string>('');
    const [docketOpen, setDocketOpen] = useState<boolean>(false);
    const [docketLoading, setDocketLoading] = useState<boolean>(false);
    const docketRef = useRef<HTMLDivElement>(null);

    const [preview, setPreview] = useState<any>(null);
    const [previewing, setPreviewing] = useState<boolean>(false);
    // Live pricing progress from the streamed preview. Null when idle.
    const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
    const [generating, setGenerating] = useState<boolean>(false);
    // Generation has its own progress so the two bars cannot be confused: one prices,
    // the other writes.
    const [genProgress, setGenProgress] = useState<{ processed: number; total: number } | null>(null);

    // Weights the operator has typed for individual dockets, keyed by docket number.
    // A consignment is weighed as a whole at clearing and that figure routinely differs
    // from the sum of its shipments' weights, which is all the system can infer.
    // Held as strings so a half-typed "1." does not become NaN mid-keystroke.
    const [docketWeights, setDocketWeights] = useState<Record<string, string>>({});

    // Saved weight sheets for this client. NOT draft invoices: generating creates an
    // invoice with status `draft` which CLAIMS its shipments, so it cannot be used as
    // a scratchpad. A sheet claims nothing.
    const [sheets, setSheets] = useState<any[]>([]);
    const [sheetsOpen, setSheetsOpen] = useState(false);
    const [includeUsedSheets, setIncludeUsedSheets] = useState(false);
    const [savingSheet, setSavingSheet] = useState(false);
    const [saveSheetOpen, setSaveSheetOpen] = useState(false);
    const [sheetName, setSheetName] = useState('');
    // The sheet the weights on screen came from, so generating can record which sheet
    // fed the invoice.
    const [loadedSheetId, setLoadedSheetId] = useState<number | null>(null);
    // "23 of 27 applied, 4 not in this preview" — shown after a load rather than
    // silently dropping the ones that found no docket.
    const [sheetApplyNote, setSheetApplyNote] = useState<string>('');

    const [invoices, setInvoices] = useState<any[]>([]);
    const [listLoader, setListLoader] = useState<boolean>(true);
    const [search, setSearch] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');

    // Bulk state correction, opened from the "could not be priced" panel.
    const [fixStates, setFixStates] = useState<any[] | null>(null);
    const [stateChoices, setStateChoices] = useState<Record<string, string>>({});

    // Per-shipment correction. A value like "Nigeria" is not a misspelled state but
    // a country, so its shipments are bound for a dozen different places and there
    // is no single answer for the group — each one has to be set on its own.
    const [expandedState, setExpandedState] = useState<string | null>(null);
    const [stateRows, setStateRows] = useState<Record<string, any[]>>({});
    const [stateRowsLoading, setStateRowsLoading] = useState<string>('');
    const [shipmentChoices, setShipmentChoices] = useState<Record<string, string>>({});
    const [allStates, setAllStates] = useState<string[]>([]);
    const [fixing, setFixing] = useState<boolean>(false);
    const [stateSearch, setStateSearch] = useState<Record<string, string>>({});
    const [openStateRow, setOpenStateRow] = useState<string | null>(null);

    const [noDocket, setNoDocket] = useState<any | null>(null);
    const [noDocketLoading, setNoDocketLoading] = useState<boolean>(false);
    const [inferred, setInferred] = useState<any[] | null>(null);
    const [detail, setDetail] = useState<any>(null);
    const [detailLoader, setDetailLoader] = useState<boolean>(false);
    const [busy, setBusy] = useState<string>('');

    // The preview is priced in the client's current currency; a generated invoice
    // keeps the currency it was raised in, so the two are resolved separately.
    const previewSymbol: string = preview?.currency_symbol || symbolFor(preview?.currency);
    const detailSymbol: string = symbolFor(detail?.invoice?.currency);
    const selectedClient = clients.find((c: any) => String(c.id) === String(clientId));

    const loadClients = useCallback(async () => {
        try {
            const res = await SuperAdminApis.getBillingClients();
            if (res?.success) {
                setClients(res.data || []);
                if (res.data?.length) setChosen((prev) => prev.length ? prev : [res.data[0].id]);
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not load billing clients'));
        }
    }, []);

    const loadCompanies = useCallback(async () => {
        try {
            const res = await SuperAdminApis.getInvoiceCompanies();
            // Only the ones switched on: a company retired mid-year stays on the
            // invoices it already issued but must not be pickable for new ones.
            if (res?.success) setCompanies((res.data || []).filter((c: any) => c.is_active));
        } catch (e) {
            // Not fatal — without the list every invoice is simply issued as Skynet.
            setCompanies([]);
        }
    }, []);

    const loadInvoices = useCallback(async () => {
        setListLoader(true);
        try {
            const res = await SuperAdminApis.getClientInvoices({
                client_id: clientId,
                status: statusFilter,
                search,
                per_page: 20,
            });
            if (res?.success) setInvoices(res.data?.data || []);
        } catch (e) {
            toast.error(apiError(e, 'Could not load invoices'));
        } finally {
            setListLoader(false);
        }
    }, [clientId, statusFilter, search]);

    // Server-side search so this stays usable when a client has thousands of dockets.
    useEffect(() => {
        if (!clientId) return;
        let cancelled = false;
        setDocketLoading(true);

        const t = setTimeout(async () => {
            try {
                const res = await SuperAdminApis.getBillingDockets(clientId, docketQuery, chosen);
                if (!cancelled && res?.success) setDockets(res.data || []);
            } catch (e) {
                if (!cancelled) setDockets([]);
            } finally {
                if (!cancelled) setDocketLoading(false);
            }
        }, 300);

        return () => { cancelled = true; clearTimeout(t); };
    }, [clientId, docketQuery, chosen]);

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (docketRef.current && !docketRef.current.contains(e.target as Node)) setDocketOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (clientPickerRef.current && !clientPickerRef.current.contains(e.target as Node)) setClientOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    useEffect(() => { loadClients(); }, [loadClients]);
    useEffect(() => { loadCompanies(); }, [loadCompanies]);
    useEffect(() => { if (clientId) loadInvoices(); }, [clientId, statusFilter, loadInvoices]);

    // Typed weights belong to one client's dockets. Carrying them to another client
    // could silently attach a figure to an unrelated docket of the same number.
    //
    // Restored from the browser rather than simply cleared: an hour of keying should
    // not be lost to a refresh or a mis-click. This is only insurance — it never
    // leaves this machine and is not the saved sheets below.
    useEffect(() => {
        setLoadedSheetId(null);
        setSheetApplyNote('');

        if (!clientId) {
            setDocketWeights({});
            return;
        }

        try {
            const cached = window.localStorage.getItem(`invoice-weights:${clientId}`);
            setDocketWeights(cached ? JSON.parse(cached) : {});
        } catch {
            // Private browsing, cleared site data, quota — none of which should stop
            // the screen working.
            setDocketWeights({});
        }
    }, [clientId]);

    useEffect(() => {
        if (!clientId) return;

        try {
            if (Object.keys(docketWeights).length === 0) {
                window.localStorage.removeItem(`invoice-weights:${clientId}`);
            } else {
                window.localStorage.setItem(`invoice-weights:${clientId}`, JSON.stringify(docketWeights));
            }
        } catch {
            // See above.
        }
    }, [clientId, docketWeights]);

    const loadSheets = useCallback(async () => {
        if (!clientId) { setSheets([]); return; }

        try {
            const res = await SuperAdminApis.listInvoiceWeightSheets({
                client_id: clientId,
                include_used: includeUsedSheets ? 1 : 0,
            });
            setSheets(res?.data || []);
        } catch {
            setSheets([]);
        }
    }, [clientId, includeUsedSheets]);

    useEffect(() => { loadSheets(); }, [loadSheets]);

    /** Save what is typed, under a name. Saving again under the same name replaces it. */
    const saveSheet = async () => {
        const weights = weightOverrides();

        if (Object.keys(weights).length === 0) {
            toast.warn('Nothing to save — no weight differs from what the shipments add up to.');
            return;
        }

        if (!sheetName.trim()) {
            toast.warn('Give these weights a name.');
            return;
        }

        setSavingSheet(true);

        try {
            const res = await SuperAdminApis.saveInvoiceWeightSheet({
                client_id: clientId,
                name: sheetName.trim(),
                weights,
                selection: {
                    from, to,
                    manifest_id: docket || null,
                    delivered_only: deliveredOnly,
                    exclude_returns: excludeReturns,
                    require_docket: requireDocket,
                    company_id: companyId || null,
                },
            });

            if (!res?.success) throw new Error(res?.message || 'Could not save');

            toast.success(res.message);
            setLoadedSheetId(res?.data?.id ?? null);
            setSaveSheetOpen(false);
            setSheetName('');
            loadSheets();
        } catch (e) {
            toast.error(apiError(e, 'Could not save these weights'));
        } finally {
            setSavingSheet(false);
        }
    };

    /**
     * Drop a saved sheet's weights back into the boxes.
     *
     * Only the dockets actually on screen are applied; the server is told what is on
     * screen so it can report the rest. A weight with no docket to land on is reported,
     * never silently dropped — the difference between "23 of 27 applied" and "27
     * applied" is a wrong invoice.
     */
    const applySheet = async (sheet: any) => {
        try {
            const dockets = (preview?.by_docket || []).map((d: any) => d.docket);

            const res = await SuperAdminApis.loadInvoiceWeightSheet(sheet.id, { dockets });

            if (!res?.success) throw new Error(res?.message || 'Could not load');

            const { weights, applied, total, missing } = res.data;

            setDocketWeights((prev) => {
                const next = { ...prev };
                Object.entries(weights || {}).forEach(([d, w]) => { next[d] = String(w); });
                return next;
            });

            setLoadedSheetId(sheet.id);
            setSheetsOpen(false);

            if (!preview) {
                setSheetApplyNote(
                    `Loaded "${sheet.name}" (${total} docket weight(s)). Run a preview and they will be applied.`,
                );
                toast.success(`Loaded "${sheet.name}"`);
                return;
            }

            setSheetApplyNote(
                applied === total
                    ? `Applied all ${total} saved weight(s) from "${sheet.name}".`
                    : `Applied ${applied} of ${total} saved weight(s) from "${sheet.name}". `
                      + `${missing.length} docket(s) are not in this preview: ${missing.slice(0, 6).join(', ')}`
                      + `${missing.length > 6 ? ` and ${missing.length - 6} more` : ''}.`,
            );

            toast.success(`Applied ${applied} of ${total} saved weight(s)`);
        } catch (e) {
            toast.error(apiError(e, 'Could not load these weights'));
        }
    };

    const removeSheet = async (sheet: any) => {
        try {
            const res = await SuperAdminApis.deleteInvoiceWeightSheet(sheet.id);
            if (!res?.success) throw new Error(res?.message || 'Could not delete');

            toast.success(res.message);
            if (loadedSheetId === sheet.id) setLoadedSheetId(null);
            loadSheets();
        } catch (e) {
            toast.error(apiError(e, 'Could not delete these weights'));
        }
    };

    /**
     * Only weights that differ from what the shipments sum to are sent. An untouched
     * box must not be transmitted as an override — the backend would then treat it as
     * a deliberate figure and freeze it, rather than tracking the shipments.
     */
    const weightOverrides = () => {
        const out: Record<string, number> = {};

        (preview?.by_docket || []).forEach((d: any) => {
            const typed = docketWeights[d.docket];
            if (typed === undefined || typed === '') return;

            const n = Number(typed);
            if (isNaN(n) || n < 0) return;
            if (Math.abs(n - Number(d.weight)) < 0.005) return;

            out[d.docket] = n;
        });

        return out;
    };

    /**
     * The current selection.
     *
     * `requireDocketOverride` exists because the docket question runs a preview in
     * the same tick as it sets the answer — and React state is not yet updated then,
     * so reading it from state would send the previous answer.
     */
    const selection = (requireDocketOverride?: boolean) => ({
        client_id: clientId,
        // Only sent when more than one is chosen, so a single-client request stays
        // byte-identical to what it was before.
        ...(chosen.length > 1 ? { client_ids: chosen } : {}),
        // A docket is billed whole, so the dates are left out rather than silently
        // intersected with it — the backend takes the docket's own span as the period.
        ...(docket ? { manifest_id: docket } : { from, to }),
        delivered_only: deliveredOnly,
        exclude_returns: excludeReturns,
        require_docket: requireDocketOverride ?? requireDocket,
        // Letterhead. Omitted entirely when nothing is picked, so a default invoice
        // request is byte-identical to what it was before companies existed.
        ...(companyId ? { company_id: companyId } : {}),
        docket_weights: weightOverrides(),
    });

    /**
     * The preview as it stands with the typed weights applied.
     *
     * Recomputed here so the figures move as the operator types instead of after a
     * round trip — a full re-preview of a two-month period per keystroke is not
     * workable. The arithmetic is deliberately trivial (weight x rate for per-kg,
     * flat charges untouched), and generation recomputes everything server-side from
     * the same overrides, so the server stays the authority on what is billed.
     */
    const view = React.useMemo(() => {
        if (!preview) return null;

        const rows = (preview.by_docket || []).map((d: any) => {
            const typed = docketWeights[d.docket];
            const n = typed === undefined || typed === '' ? NaN : Number(typed);
            const chargeWeight = isNaN(n) || n < 0 ? Number(d.weight) : n;
            const overridden = Math.abs(chargeWeight - Number(d.weight)) > 0.005;

            const charges = (d.charges || []).map((c: any) => (
                c.type === 'flat'
                    ? c
                    : {
                        ...c,
                        amount: Math.round(chargeWeight * Number(c.rate) * 100) / 100,
                        calculation: `${chargeWeight}kg × ${formatMoney(c.rate, previewSymbol)}`,
                    }
            ));

            const chargesTotal = charges.reduce((sum: number, c: any) => sum + Number(c.amount), 0);

            return {
                ...d,
                charges,
                charge_weight: chargeWeight,
                weight_overridden: overridden,
                charges_total: Math.round(chargesTotal * 100) / 100,
                total: Math.round((Number(d.lastmile) + chargesTotal) * 100) / 100,
            };
        });

        // Per charge: the dockets carrying it, the weight it was billed on, and the sum.
        const chargeTotals = (preview.charge_totals || []).map((c: any) => {
            let amount = 0;
            let weight = 0;
            let dockets = 0;

            rows.forEach((r: any) => {
                const hit = (r.charges || []).find((x: any) => x.name === c.name);
                if (!hit) return;

                dockets++;
                amount += Number(hit.amount);
                // Flat charges are not billed on weight, so they contribute none.
                if (hit.type !== 'flat') weight += Number(r.charge_weight);
            });

            return {
                ...c,
                dockets,
                weight: Math.round(weight * 100) / 100,
                amount: Math.round(amount * 100) / 100,
            };
        });

        const chargesSubtotal = Math.round(chargeTotals.reduce((sum: number, c: any) => sum + Number(c.amount), 0) * 100) / 100;
        const lastmile = Number(preview.lastmile_subtotal);
        const subtotal = Math.round((lastmile + chargesSubtotal) * 100) / 100;
        const vatAmount = Math.round(subtotal * Number(preview.vat_rate || 0)) / 100; // rate is a percentage, so x/100 then rounded to cents

        return {
            ...preview,
            by_docket: rows,
            charge_totals: chargeTotals,
            charges_subtotal: chargesSubtotal,
            subtotal,
            vat_amount: vatAmount,
            total_amount: Math.round((subtotal + vatAmount) * 100) / 100,
            // True when anything has been retyped — drives the "figures include your
            // edits" note so a changed total is never unexplained.
            has_overrides: rows.some((r: any) => r.weight_overridden),
        };
    }, [preview, docketWeights, previewSymbol]);

    /**
     * How many shipments one request prices.
     *
     * Not a tuning knob — a survival one. The platform closes a request at roughly
     * thirty seconds whatever PHP's own timer says, which is what truncated the
     * streamed preview at ~30,000 of 53,636 shipments. A window of this size finishes
     * in a few seconds on any period, so the run is bounded by how many windows it
     * takes rather than by how long one request may last.
     */
    const BATCH_SIZE = 8000;

    const closeFixStates = () => {
        setFixStates(null);
        setShipmentChoices({});
        setStateRows({});
        setExpandedState(null);
    };

    /** Opens the corrector with one row per distinct unmapped state. */
    const openFixStates = async () => {
        setStateChoices({});
        setStateSearch({});
        // Cleared rather than reused: correcting some of a group changes which
        // shipments are still under it, so cached rows would be stale.
        setShipmentChoices({});
        setStateRows({});
        setExpandedState(null);
        setFixStates(preview?.unmapped_states || []);

        if (!allStates.length) {
            try {
                const res = await SuperAdminApis.getBillingStates();
                if (res?.success) setAllStates(res.data || []);
            } catch (e) {
                toast.error(apiError(e, 'Could not load the list of states'));
            }
        }
    };

    /**
     * Open one group and fetch the shipments under it.
     *
     * Loaded on demand rather than with the preview: most groups are a misspelling
     * that the operator fixes in one go without ever needing to see the rows.
     */
    const toggleStateGroup = async (value: string) => {
        if (expandedState === value) { setExpandedState(null); return; }

        setExpandedState(value);

        if (stateRows[value]) return;

        setStateRowsLoading(value);
        try {
            const res = await SuperAdminApis.getUnmappedStateShipments({
                ...selection(), state: value, per_page: 500,
            });

            if (res?.success) {
                setStateRows((prev) => ({ ...prev, [value]: res.data?.data || [] }));
            } else {
                toast.warn(res?.message || 'Could not load those shipments');
                setExpandedState(null);
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not load those shipments'));
            setExpandedState(null);
        } finally {
            setStateRowsLoading('');
        }
    };

    /** How many shipments in this group have been given a state of their own. */
    const setIndividually = (value: string) =>
        (stateRows[value] || []).filter((r: any) => shipmentChoices[r.id]).length;

    /**
     * Write the corrections, then re-run the preview so the figures reflect them.
     *
     * Both kinds travel in one request: whole-value mappings for the groups that
     * are simply written wrongly, and individual shipments for the groups where
     * there is no single right answer.
     */
    const applyStateFixes = async () => {
        const mappings = Object.entries(stateChoices)
            .filter(([, to]) => !!to)
            .map(([from, to]) => ({ from, to }));

        const shipments = Object.entries(shipmentChoices)
            .filter(([, to]) => !!to)
            .map(([id, to]) => ({ id: Number(id), to }));

        if (!mappings.length && !shipments.length) {
            toast.warn('Choose a state for at least one row first');
            return;
        }

        const bulkTotal = (fixStates || [])
            .filter((u: any) => stateChoices[u.state])
            .reduce((n: number, u: any) => n + Number(u.count || 0), 0);

        const parts = [
            bulkTotal ? `${bulkTotal} shipment(s) across ${mappings.length} state value(s)` : '',
            shipments.length ? `${shipments.length} shipment(s) chosen individually` : '',
        ].filter(Boolean);

        if (!window.confirm(
            `Update ${parts.join(' and ')}?\n\n`
            + `This changes the shipments themselves, not just this invoice — the new state shows `
            + `everywhere in the system. Only shipments in this preview's client and period are touched.`
        )) return;

        setFixing(true);
        try {
            const res = await SuperAdminApis.fixUnmappedStates({ ...selection(), mappings, shipments });

            if (res?.success) {
                toast.success(res.message);
                setFixStates(null);
                setStateChoices({});
                setShipmentChoices({});
                setStateRows({});
                setExpandedState(null);
                runPreview(requireDocket);   // recalculate with the corrected states
            } else {
                toast.warn(res?.message || 'Could not update those states');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not update those states'));
        } finally {
            setFixing(false);
        }
    };

    const openNoDocket = async () => {
        setNoDocketLoading(true);
        setNoDocket({ data: [], total: preview?.no_docket ?? 0 });
        try {
            const res = await SuperAdminApis.getNoDocketShipments({ ...selection(), per_page: 100 });
            if (res?.success) setNoDocket(res.data);
            else toast.warn(res?.message || 'Could not load those shipments');
        } catch (e) {
            toast.error(apiError(e, 'Could not load those shipments'));
        } finally {
            setNoDocketLoading(false);
        }
    };

    const runPreview = async (requireDocketOverride?: boolean) => {
        if (!clientId) { toast.warn('Choose a client'); return; }
        if (!docket && from && to && to < from) {
            toast.warn('The "To" date is before the "From" date — swap them and try again.');
            return;
        }

        setPreviewing(true);
        setPreview(null);
        // Shown straight away rather than after the first window comes back: counting a
        // long period takes a few seconds on its own, and a dead button for that long is
        // exactly what makes people think it has hung.
        setProgress({ processed: 0, total: 0 });

        try {
            // Each client is walked separately — a window cannot span clients, because
            // each is priced against its own zone card. Their accumulations are kept
            // apart so VAT and docket charges can be worked out per agreement.
            const clientIds = chosen.length ? chosen : [clientId];
            const perClient: any[] = [];

            let windows = 0;
            let expected = 0;
            let grandTotal = 0;

            for (const cid of clientIds) {
            // Accumulated across windows. Every one of these is bounded by zones,
            // dockets or a fixed sample cap — none grows with the number of shipments,
            // which is what lets a period of any length be assembled in the browser.
            const acc: any = {
                client_id: cid,
                total_shipments: 0, total_weight: 0, lastmile: 0,
                by_zone: {} as Record<string, any>,
                by_docket: {} as Record<string, any>,
                unmapped: {} as Record<string, any>,
                resolved_from: {} as Record<string, number>,
                sample_lines: [] as any[],
                inferred_lines: [] as any[],
                zones_without_rate: [] as string[],
            };

            let cursor: any = null;

            do {
                const res = await SuperAdminApis.previewClientInvoiceBatch({
                    ...selection(requireDocketOverride), batch_client_id: cid, cursor, limit: BATCH_SIZE,
                });

                if (!res?.success) throw new Error(res?.message || 'Preview failed');

                const d = res.data;
                windows++;

                if (d.total !== null && d.total !== undefined) expected = Number(d.total);

                acc.total_shipments += d.total_shipments;
                acc.total_weight += Number(d.total_weight);
                acc.lastmile += Number(d.lastmile);

                (d.by_zone || []).forEach((z: any) => {
                    const k = z.zone;
                    acc.by_zone[k] ??= { zone: k, shipments: 0, weight: 0, amount: 0 };
                    acc.by_zone[k].shipments += z.shipments;
                    acc.by_zone[k].weight += Number(z.weight);
                    acc.by_zone[k].amount += Number(z.amount);
                });

                // A docket's shipments can straddle two windows, so they are summed
                // here and only charged once, after the last window.
                (d.by_docket || []).forEach((x: any) => {
                    const k = x.docket;
                    acc.by_docket[k] ??= { docket: k, shipments: 0, weight: 0, lastmile: 0 };
                    acc.by_docket[k].shipments += x.shipments;
                    acc.by_docket[k].weight += Number(x.weight);
                    acc.by_docket[k].lastmile += Number(x.lastmile);
                });

                (d.unmapped || []).forEach((u: any) => {
                    const k = u.state;
                    acc.unmapped[k] ??= { ...u, count: 0, samples: [] };
                    acc.unmapped[k].count += u.count;
                    acc.unmapped[k].samples = [...acc.unmapped[k].samples, ...(u.samples || [])].slice(0, 5);
                });

                Object.entries(d.resolved_from || {}).forEach(([k, v]: any) => {
                    acc.resolved_from[k] = (acc.resolved_from[k] || 0) + Number(v);
                });

                acc.sample_lines = [...acc.sample_lines, ...(d.sample_lines || [])].slice(0, 25);
                acc.inferred_lines = [...acc.inferred_lines, ...(d.inferred_lines || [])].slice(0, 500);
                (d.zones_without_rate || []).forEach((z: string) => {
                    if (!acc.zones_without_rate.includes(z)) acc.zones_without_rate.push(z);
                });

                setProgress({ processed: grandTotal + acc.total_shipments, total: expected });
                cursor = d.next_cursor;
            } while (cursor !== null && cursor !== undefined);

            grandTotal += acc.total_shipments;

            perClient.push({
                client_id: cid,
                total_shipments: acc.total_shipments,
                total_weight: Math.round(acc.total_weight * 100) / 100,
                lastmile: Math.round(acc.lastmile * 100) / 100,
                by_zone: Object.values(acc.by_zone),
                by_docket: Object.values(acc.by_docket),
                unmapped: Object.values(acc.unmapped),
                resolved_from: acc.resolved_from,
                sample_lines: acc.sample_lines,
                inferred_lines: acc.inferred_lines,
                zones_without_rate: acc.zones_without_rate,
            });
            } // end per-client walk

            // The last window is followed by the finalise call, which prices the docket
            // charges. Shown as a full bar rather than a stall on the final percent.
            setProgress({ processed: expected || grandTotal, total: expected || grandTotal });

            // Docket charges, the whole-period skip counts and the totals are worked
            // out once, from the merged set.
            // One client keeps the original flat contract; several are sent as a set
            // so the backend can charge and apply VAT per agreement before combining.
            const res = await SuperAdminApis.previewClientInvoiceFinalise(
                perClient.length > 1
                    ? { ...selection(requireDocketOverride), per_client: perClient }
                    : { ...selection(requireDocketOverride), ...perClient[0] }
            );

            if (!res?.success) throw new Error(res?.message || 'Preview failed');

            setPreview(res.data);
            if (!res.data.total_shipments) toast.info('Nothing billable in this period');
            else if (windows > 1) toast.success(`Priced ${grandTotal.toLocaleString()} shipments in ${windows} batches`);
        } catch (e) {
            toast.error(apiError(e, 'Preview failed'));
        } finally {
            setPreviewing(false);
            setProgress(null);
        }
    };

    // ── Return billing ───────────────────────────────────────

    /**
     * The sheet the client fills in. Built here rather than fetched so it can never
     * drift from the parser below — the headings this writes are the headings that
     * function reads.
     */
    const downloadReturnTemplate = () => {
        const sheet = XLSX.utils.aoa_to_sheet([
            ['Tracking Number', 'Status', 'Customs Duty'],
        ]);
        sheet['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 14 }];

        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Returns');
        XLSX.writeFile(book, 'return_billing_template.xlsx');
    };

    /**
     * Read the uploaded sheet.
     *
     * The header row is found rather than assumed: these files come back with a
     * title above the table as often as not. Columns are matched on a fragment of
     * their name so the client's own wording ("DESTROY/RETURN", "CUSTOMS DUTY")
     * works without being retyped to match ours.
     */
    const parseReturnFile = async (file: File | null) => {
        if (!file) return;

        setReturnPreview(null);
        setReturnRows([]);
        setReturnColumns(null);

        try {
            const book  = XLSX.read(await file.arrayBuffer(), { type: 'array' });
            const sheet = book.Sheets[book.SheetNames[0]];
            const grid: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' });

            const norm = (v: any) => String(v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

            let headerRow = -1;
            let cols: any = null;

            for (let i = 0; i < Math.min(grid.length, 10); i++) {
                const cells = (grid[i] || []).map(norm);

                // No weight: it is read off the shipment record, so a Weight column
                // on an older sheet is simply ignored rather than billed on.
                const found = {
                    tracking: cells.findIndex((c) => c.includes('tracking') || c.includes('shipment') || c.includes('waybill') || c.includes('awb')),
                    status:   cells.findIndex((c) => c.includes('status') || c.includes('destroy') || c.includes('return')),
                    duty:     cells.findIndex((c) => c.includes('duty') || c.includes('customs')),
                };

                if (found.tracking >= 0 && found.duty >= 0) { headerRow = i; cols = found; break; }
            }

            if (headerRow < 0) {
                toast.error('Could not find the columns on that sheet. It needs a header row with Tracking Number, Weight, Status and Customs Duty.');
                return;
            }

            const rows: any[] = [];

            for (let i = headerRow + 1; i < grid.length; i++) {
                const row = grid[i] || [];
                const tracking = String(row[cols.tracking] ?? '').trim();

                // The totals line at the foot of these sheets has no tracking number.
                if (!tracking || norm(tracking) === 'total') continue;

                rows.push({
                    tracking,
                    status: String(row[cols.status] ?? '').trim(),
                    customs_duty: Number(String(row[cols.duty] ?? '').replace(/[^0-9.\-]/g, '')) || 0,
                });
            }

            if (!rows.length) {
                toast.warn('That sheet has headers but no rows under them.');
                return;
            }

            setReturnFile(file.name);
            setReturnRows(rows);
            setReturnColumns({
                ...cols,
                // Stated back so a sheet whose Status column was never found is
                // obvious BEFORE it prices every row as duty-only.
                headers: (grid[headerRow] || []).map((h: any) => String(h ?? '')),
            });
            toast.success(`${rows.length} row(s) read from ${file.name}`);
        } catch (e) {
            toast.error('Could not read that file. It must be .xlsx, .xls or .csv');
        }
    };

    const runReturnPreview = async () => {
        if (!returnRows.length) { toast.warn('Upload a sheet first'); return; }

        setReturnBusy('preview');
        try {
            const res = await SuperAdminApis.previewReturnInvoice({
                client_id: clientId,
                rows: returnRows,
                ...(companyId ? { company_id: companyId } : {}),
            });

            if (res?.success) {
                setReturnPreview(res.data);
            } else {
                setReturnPreview(null);
                toast.warn(res?.message || 'Could not price that sheet', { autoClose: 12000 });
            }
        } catch (e) {
            setReturnPreview(null);
            toast.error(apiError(e, 'Could not price that sheet'), { autoClose: 12000 });
        } finally {
            setReturnBusy('');
        }
    };

    const runReturnGenerate = async () => {
        if (!returnPreview?.lines?.length) { toast.warn('Price the sheet first'); return; }

        const t = returnPreview.totals;

        if (!window.confirm(
            `Generate a return invoice for ${t.shipments} shipment(s) totalling `
            + `${formatMoney(t.total, returnPreview.currency_symbol)}?\n\n`
            + `${t.returns} returned (priced) · ${t.duty_only} billed for customs duty only.\n\n`
            + 'These shipments will be locked to this invoice and cannot be return billed again unless it is voided.'
        )) return;

        setReturnBusy('generate');
        try {
            const res = await SuperAdminApis.generateReturnInvoice({
                client_id: clientId,
                rows: returnRows,
                ...(companyId ? { company_id: companyId } : {}),
            });

            if (res?.success) {
                toast.success(res.message, { autoClose: 8000 });
                setReturnPreview(null);
                setReturnRows([]);
                setReturnFile('');
                setReturnColumns(null);
                loadInvoices();
            } else {
                toast.warn(res?.message || 'Could not generate the invoice', { autoClose: 12000 });
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not generate the invoice'), { autoClose: 12000 });
        } finally {
            setReturnBusy('');
        }
    };

    /**
     * The letterhead picker.
     *
     * Held as one element and rendered by both streams rather than written twice:
     * which company an invoice is issued under has nothing to do with how it was
     * priced, so last mile and returns must offer exactly the same choice.
     */
    const letterhead = companies.length === 0 ? null : (() => {
        const picked = companies.find((c: any) => String(c.id) === String(companyId));

        return (
            <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b">
                <div className="w-full max-w-xs">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Issue under
                    </label>
                    <select
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                    >
                        <option value="">Skynet Worldwide Express Nigeria (default)</option>
                        {companies.map((c: any) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {picked?.logo && (
                    <img src={picked.logo} alt={picked.name}
                        className="h-10 w-auto max-w-[130px] object-contain mt-4" />
                )}

                <p className="text-xs text-gray-500 mt-4 flex-1 min-w-[220px]">
                    {picked
                        ? <>The invoice is issued as <strong className="text-gray-700">{picked.name}</strong>{' '}
                            — its name{picked.logo ? ' and logo' : ''} replace Skynet's as the sender on the PDF
                            and the Excel. What is billed does not change.</>
                        : <>Invoices are issued as Skynet. Companies are added under{' '}
                            <strong className="text-gray-700">Billing Zones &amp; Rates</strong>.</>}
                </p>
            </div>
        );
    })();

    const runGenerate = async () => {
        if (!preview?.total_shipments) { toast.warn('Run a preview with billable shipments first'); return; }
        if (!window.confirm(
            `Generate an invoice for ${view.total_shipments} shipment(s) totalling ${formatMoney(view.total_amount, previewSymbol)}?\n\n` +
            `These shipments will be locked to this invoice and cannot be billed again unless it is voided.`
        )) return;

        setGenerating(true);
        setGenProgress({ processed: 0, total: 0 });

        // Held so the catch block can name the draft that was left behind. Building
        // across several requests means it cannot be atomic — an interrupted run
        // leaves a draft, which is why it is discardable from the list.
        let draft: any = null;

        try {
            const started = await SuperAdminApis.generateClientInvoiceStart(selection());
            if (!started?.success) throw new Error(started?.message || 'Could not start the invoice');

            draft = started.data;
            const total = draft.total || preview.total_shipments;

            // The count is known the moment the draft opens, so the bar starts real
            // rather than waiting for the first batch to land.
            setGenProgress({ processed: 0, total });

            // Same shape as the preview: one client at a time, cursored within each,
            // all writing into the one draft.
            const genClientIds = chosen.length ? chosen : [clientId];
            let written = 0;

            for (const cid of genClientIds) {
                let cursor: any = null;

                do {
                    const res = await SuperAdminApis.generateClientInvoiceBatch({
                        ...selection(), invoice_id: draft.invoice_id, batch_client_id: cid, cursor, limit: BATCH_SIZE,
                    });

                    if (!res?.success) throw new Error(res?.message || 'Could not write this batch');

                    written += res.data.written;
                    setGenProgress({ processed: written, total });
                    cursor = res.data.next_cursor;
                } while (cursor !== null && cursor !== undefined);
            }

            const done = await SuperAdminApis.generateClientInvoiceFinalise({
                ...selection(),
                invoice_id: draft.invoice_id,
                // Recorded against the invoice so a disputed figure can be traced back
                // to the sheet it was keyed on. The sheet is kept, not deleted.
                ...(loadedSheetId ? { weight_sheet_id: loadedSheetId } : {}),
            });

            if (!done?.success) throw new Error(done?.message || 'Could not finalise the invoice');

            toast.success(done.message);
            setPreview(null);
            setDocketWeights({});
            setLoadedSheetId(null);
            setSheetApplyNote('');
            loadSheets();
            loadInvoices();
        } catch (e) {
            toast.error(apiError(e, 'Could not generate invoice'));

            if (draft?.invoice_number) {
                toast.warn(
                    `${draft.invoice_number} was left unfinished. It is in the list below marked Draft — `
                    + 'discard it to release its shipments.',
                    { autoClose: 12000 },
                );
            }

            // Show the half-built draft immediately rather than making them refresh.
            loadInvoices();
        } finally {
            setGenerating(false);
            setGenProgress(null);
        }
    };

    /** Throw away an unfinished draft and release the shipments it locked. */
    const discardDraft = async (invoice: any) => {
        if (!window.confirm(
            `Discard ${invoice.invoice_number}?\n\n`
            + 'It was never finished, so nothing was billed. Its shipments go back into the billable pool.'
        )) return;

        try {
            const res = await SuperAdminApis.discardDraftInvoice(invoice.id);
            if (res?.success) {
                toast.success(res.message);
                loadInvoices();
            } else {
                toast.warn(res?.message || 'Could not discard the draft');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not discard the draft'));
        }
    };

    const openDetail = async (invoice: any) => {
        setDetailLoader(true);
        setDetail({ invoice });
        try {
            const res = await SuperAdminApis.getClientInvoice(invoice.id);
            if (res?.success) setDetail(res.data);
        } catch (e) {
            toast.error(apiError(e, 'Could not load invoice'));
        } finally {
            setDetailLoader(false);
        }
    };

    // The full line export is streamed CSV, not xlsx — building a spreadsheet for a
    // 22,000-line invoice needs more memory than the server has, so it used to fail
    // on exactly the invoices worth exporting. The PDF is a summary and stays a PDF.
    const download = async (invoice: any, kind: 'xlsx' | 'pdf') => {
        setBusy(`${kind}-${invoice.id}`);
        try {
            const blob = kind === 'xlsx'
                ? await SuperAdminApis.exportClientInvoice(invoice.id)
                : await SuperAdminApis.downloadClientInvoicePdf(invoice.id);
            downloadBlob(blob, `${invoice.invoice_number}.${kind === 'xlsx' ? 'csv' : 'pdf'}`);
        } catch (e) {
            toast.error(apiError(e, 'Download failed'));
        } finally {
            setBusy('');
        }
    };

    const voidInvoice = async (invoice: any) => {
        const reason = window.prompt(
            `Void ${invoice.invoice_number}?\n\nIts ${invoice.total_shipments} shipment(s) become billable again. The invoice and its lines are kept for the record.\n\nReason:`
        );
        if (reason === null) return;

        setBusy(`void-${invoice.id}`);
        try {
            const res = await SuperAdminApis.voidClientInvoice(invoice.id, { reason });
            if (res?.success) {
                toast.success(res.message);
                setDetail(null);
                loadInvoices();
            } else {
                toast.warn(res?.message || 'Could not void invoice');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not void invoice'));
        } finally {
            setBusy('');
        }
    };

    /**
     * Delete a voided invoice for good.
     *
     * Typed confirmation rather than a plain OK: this is the only action here that
     * destroys a record, and it is one click from Void in the same row.
     */
    const deleteInvoice = async (invoice: any) => {
        const typed = window.prompt(
            `Delete ${invoice.invoice_number} permanently?\n\n`
            + `Its ${invoice.total_shipments} line(s) go with it. The invoice is already void, so no shipment `
            + `is affected — but the record that they were once billed and released is lost.\n\n`
            + `Type the invoice number to confirm:`
        );

        if (typed === null) return;

        if (typed.trim().toUpperCase() !== String(invoice.invoice_number).toUpperCase()) {
            toast.warn('That did not match the invoice number. Nothing was deleted.');
            return;
        }

        setBusy(`delete-${invoice.id}`);
        try {
            const res = await SuperAdminApis.deleteClientInvoice(invoice.id);
            if (res?.success) {
                toast.success(res.message);
                setDetail(null);
                loadInvoices();
            } else {
                toast.warn(res?.message || 'Could not delete invoice', { autoClose: 10000 });
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not delete invoice'), { autoClose: 10000 });
        } finally {
            setBusy('');
        }
    };

    const markStatus = async (invoice: any, status: string) => {
        setBusy(`status-${invoice.id}`);
        try {
            const res = await SuperAdminApis.updateClientInvoiceStatus(invoice.id, { status });
            if (res?.success) {
                toast.success(res.message);
                loadInvoices();
                if (detail?.invoice?.id === invoice.id) openDetail(res.data);
            } else {
                toast.warn(res?.message || 'Could not update status');
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not update status'));
        } finally {
            setBusy('');
        }
    };

    return (
        <>
            <AdminSidebar title="Client Invoices" />
            <div className="relative md:ml-64 bg-[#FAFAFA] min-h-screen">
                <AdminNavbar title="Client Invoices" />

                <div className="lg:px-10 px-6 pb-10 pt-4">

                    {/* ── Generate ─────────────────────────────── */}
                    <div className="bg-white rounded-lg shadow p-5 mb-6">
                        <h3 className="text-[17px] font-semibold text-[#1C1C1C] mb-4">Generate an invoice</h3>

                        {/* Which stream. Two genuinely different invoices, so the
                            choice is made first and the rest of the card follows it,
                            rather than being a filter buried among the others. */}
                        <div className="flex flex-wrap items-center gap-2 mb-5 pb-5 border-b">
                            {[
                                { key: 'lastmile', label: 'Last mile', icon: <Truck size={15} />,
                                  hint: 'Priced from the zone rates, over a period or a docket' },
                                { key: 'return', label: 'Return charges', icon: <CornerUpLeft size={15} />,
                                  hint: 'Priced from a sheet of returned shipments the client sends' },
                            ].map((m) => (
                                <button
                                    key={m.key}
                                    onClick={() => setBillMode(m.key as any)}
                                    title={m.hint}
                                    className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 border ${
                                        billMode === m.key
                                            ? 'bg-gray-800 text-white border-gray-800'
                                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                                >
                                    {m.icon} {m.label}
                                </button>
                            ))}
                            <span className="text-xs text-gray-500 ml-1">
                                {billMode === 'return'
                                    ? 'Built from the client\u2019s sheet: return charge on the returned rows, customs duty on all of them. Weights come from the system. No VAT.'
                                    : 'Built from what the system already knows: destination state decides the zone, the zone decides the price.'}
                            </span>
                        </div>

                        {billMode === 'lastmile' && (<>

                        {/* Docket picker. Selecting one takes over from the date range,
                            so the two can never disagree about what is being billed. */}
                        <div className="mb-3" ref={docketRef}>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Docket <span className="font-normal text-gray-400">— optional, bills the whole docket</span>
                            </label>
                            <div className="relative">
                                <div className="flex gap-2">
                                    <input
                                        value={docket || docketQuery}
                                        onChange={(e) => { setDocket(''); setDocketQuery(e.target.value); setDocketOpen(true); setPreview(null); }}
                                        onFocus={() => setDocketOpen(true)}
                                        placeholder="Search a docket number, or leave blank to bill by date…"
                                        className={`w-full px-3 py-2 border rounded-md text-sm ${docket ? 'bg-blue-50 border-blue-300 font-medium text-blue-800' : ''}`}
                                    />
                                    {docket && (
                                        <button
                                            onClick={() => { setDocket(''); setDocketQuery(''); setPreview(null); }}
                                            className="px-3 py-2 border rounded-md text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>

                                {docketOpen && !docket && (
                                    <div className="absolute z-30 mt-1 w-full bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto">
                                        {docketLoading ? (
                                            <div className="px-3 py-3 text-sm text-gray-400">Searching…</div>
                                        ) : dockets.length === 0 ? (
                                            <div className="px-3 py-3 text-sm text-gray-400">
                                                {docketQuery ? 'No docket matches that.' : 'No dockets on this client yet.'}
                                            </div>
                                        ) : dockets.map((d: any) => (
                                            <button
                                                key={d.manifest_id}
                                                onClick={() => { setDocket(d.manifest_id); setDocketOpen(false); setPreview(null); }}
                                                disabled={d.billable === 0}
                                                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 disabled:opacity-40 disabled:hover:bg-white"
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="text-sm font-medium text-gray-800">{d.manifest_id}</span>
                                                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${d.billable > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                        {d.billable > 0 ? `${d.billable} to bill` : 'fully invoiced'}
                                                    </span>
                                                </div>
                                                <div className="text-[11px] text-gray-500 mt-0.5">
                                                    {d.shipments} shipment(s){d.invoiced > 0 ? ` · ${d.invoiced} already invoiced` : ''} · {formatDate(d.first_at)} – {formatDate(d.last_at)}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                            <div ref={clientPickerRef} className="relative">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Client{chosen.length > 1 && <span className="font-normal text-gray-400"> — {chosen.length} selected</span>}
                                </label>
                                <button type="button" onClick={() => setClientOpen((o) => !o)}
                                    className="w-full px-3 py-2 border rounded-md text-sm bg-white text-left flex items-center justify-between gap-2">
                                    <span className="truncate">
                                        {chosen.length
                                            ? chosen.map((id) => clients.find((c: any) => String(c.id) === String(id))?.name).filter(Boolean).join(' + ')
                                            : <span className="text-gray-400">Choose one or more clients…</span>}
                                    </span>
                                    <ChevronDown size={15} className="text-gray-400 shrink-0" />
                                </button>

                                {clientOpen && (
                                    <div className="absolute z-30 mt-1 w-full bg-white border rounded-md shadow-lg">
                                        <div className="p-2 border-b">
                                            <input autoFocus value={clientQuery} onChange={(e) => setClientQuery(e.target.value)}
                                                placeholder="Search clients…"
                                                className="w-full px-2 py-1.5 border rounded text-xs" />
                                        </div>
                                        <div className="max-h-56 overflow-y-auto">
                                            {clients
                                                .filter((c: any) => c.name.toLowerCase().includes(clientQuery.toLowerCase()))
                                                .map((c: any) => {
                                                    const on = chosen.some((id) => String(id) === String(c.id));
                                                    // Currencies cannot be added up. The first pick sets the
                                                    // currency; anything else is blocked here rather than
                                                    // refused after a round trip.
                                                    const anchor = chosen.length ? clients.find((x: any) => String(x.id) === String(chosen[0])) : null;
                                                    const clash  = !on && anchor && c.currency !== anchor.currency;

                                                    return (
                                                        <button key={c.id} type="button" disabled={!!clash}
                                                            title={clash ? `${c.name} is billed in ${c.currency}, not ${anchor.currency}` : ''}
                                                            onClick={() => {
                                                                setChosen((prev) => {
                                                                    const next = on
                                                                        ? prev.filter((id) => String(id) !== String(c.id))
                                                                        : [...prev, c.id];
                                                                    // Never leave nothing selected.
                                                                    return next.length ? next : prev;
                                                                });
                                                                setPreview(null);
                                                            }}
                                                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 border-b last:border-b-0 ${
                                                                clash ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                                                            <span className="flex items-center gap-2">
                                                                <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                                                                    on ? 'bg-[#E04343] border-[#E04343] text-white' : 'border-gray-300'}`}>
                                                                    {on && '\u2713'}
                                                                </span>
                                                                {c.name}
                                                            </span>
                                                            <span className="text-[10px] text-gray-400">{c.currency}</span>
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${docket ? 'text-gray-300' : 'text-gray-600'}`}>From (shipment date)</label>
                                <input type="date" value={from} disabled={!!docket} onChange={(e) => { setFrom(e.target.value); setPreview(null); }}
                                    className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                            </div>
                            <div>
                                <label className={`block text-xs font-semibold mb-1 ${docket ? 'text-gray-300' : 'text-gray-600'}`}>To</label>
                                <input type="date" value={to} disabled={!!docket} onChange={(e) => { setTo(e.target.value); setPreview(null); }}
                                    className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                            </div>
                            <div className="flex items-end gap-2">
                                <button onClick={() => setAskDocket(true)} disabled={previewing}
                                    className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
                                    <Search size={15} />
                                    {previewing
                                        ? (progress && progress.total > 0
                                            ? `Pricing ${progress.processed.toLocaleString()} of ${progress.total.toLocaleString()}…`
                                            : progress && progress.processed > 0
                                                // The count is known before the total is —
                                                // better a moving number than a static word.
                                                ? `Priced ${progress.processed.toLocaleString()}…`
                                                : 'Checking…')
                                        : 'Preview'}
                                </button>
                                <button onClick={runGenerate} disabled={generating || !preview?.total_shipments}
                                    className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                                    <FileText size={15} /> {generating
                                        ? (genProgress && genProgress.total > 0
                                            ? `Billing ${genProgress.processed.toLocaleString()} of ${genProgress.total.toLocaleString()}…`
                                            : 'Generating…')
                                        : 'Generate'}
                                </button>
                            </div>
                        </div>

                        {letterhead}

                        {/* Real progress, not a spinner: the server reports every 500
                            shipments it prices, so a long period visibly advances. */}
                        {previewing && progress && (
                            <div className="mb-4">
                                <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={progress.total > 0
                                            ? 'h-full bg-gray-800 transition-all duration-200'
                                            // Until the first window returns the count there is
                                            // no percentage to draw, so the bar animates instead
                                            // of sitting at zero looking stuck.
                                            : 'h-full w-1/3 bg-gray-800 animate-pulse'}
                                        style={progress.total > 0
                                            ? { width: `${Math.min(100, Math.round((progress.processed / progress.total) * 100))}%` }
                                            : undefined}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {progress.total > 0
                                        ? `Pricing ${progress.processed.toLocaleString()} of ${progress.total.toLocaleString()} shipments — `
                                          + `${Math.min(100, Math.round((progress.processed / progress.total) * 100))}%`
                                        : 'Counting the shipments in this period…'}
                                </p>
                            </div>
                        )}

                        {/* The same bar for generation. Writing a two-month invoice
                            takes as long as pricing one, and an operator watching a
                            frozen button has no way to tell work from a hang. */}
                        {generating && genProgress && (
                            <div className="mb-4">
                                <div className="h-1.5 w-full bg-red-100 rounded-full overflow-hidden">
                                    <div
                                        className={genProgress.total > 0
                                            ? 'h-full bg-[#E04343] transition-all duration-200'
                                            : 'h-full w-1/3 bg-[#E04343] animate-pulse'}
                                        style={genProgress.total > 0
                                            ? { width: `${Math.min(100, Math.round((genProgress.processed / genProgress.total) * 100))}%` }
                                            : undefined}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {genProgress.total > 0
                                        ? `Billing ${genProgress.processed.toLocaleString()} of ${genProgress.total.toLocaleString()} shipments — `
                                          + `${Math.min(100, Math.round((genProgress.processed / genProgress.total) * 100))}%`
                                        : 'Opening the invoice…'}
                                    <span className="text-gray-400"> Leave this page open until it finishes.</span>
                                </p>
                            </div>
                        )}

                        {chosen.length > 1 && (
                            <p className="text-xs text-gray-500 mb-3">
                                Billing <strong className="text-gray-700">{chosen.length} clients</strong> onto one
                                invoice. Each keeps its own rates and VAT; the totals are combined.
                            </p>
                        )}

                        <div className="flex flex-wrap gap-5 text-sm">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={deliveredOnly}
                                    onChange={(e) => { setDeliveredOnly(e.target.checked); setPreview(null); }} />
                                Delivered (POD) only
                            </label>
                            <label className={`flex items-center gap-2 ${deliveredOnly ? 'opacity-40' : 'cursor-pointer'}`}>
                                <input type="checkbox" checked={excludeReturns} disabled={deliveredOnly}
                                    onChange={(e) => { setExcludeReturns(e.target.checked); setPreview(null); }} />
                                Exclude returns / RTS
                            </label>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Info size={13} />
                                {docket
                                    ? <>Billing docket <strong className="text-blue-700">{docket}</strong> in full — the date range is ignored.</>
                                    : 'Shipments already on a live invoice are never billed twice.'}
                            </span>
                        </div>

                        {selectedClient && (
                            <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex flex-wrap gap-x-5 gap-y-1">
                                <span>
                                    Billed in <strong className="text-gray-700">{selectedClient.currency}</strong>
                                </span>
                                <span>
                                    VAT{' '}
                                    {selectedClient.vat_enabled
                                        ? <strong className="text-gray-700">{Number(selectedClient.vat_rate)}%</strong>
                                        : <strong className="text-gray-700">off</strong>}
                                </span>
                                <span className="text-gray-400">
                                    Change these under Billing Zones &amp; Rates → Edit client
                                </span>
                            </div>
                        )}

                        </>)}

                        {billMode === 'return' && (
                            <div>
                                {letterhead}

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Client</label>
                                        <select
                                            value={clientId}
                                            onChange={(e) => {
                                                setChosen([e.target.value]);
                                                setReturnPreview(null);
                                            }}
                                            className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                        >
                                            {clients.map((c: any) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                        {/* One client, deliberately: the sheet has no client column, so
                                            there is nothing to split its rows by. */}
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            One client per return invoice — the sheet has no client column.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">The sheet</label>
                                        <div className="flex gap-2">
                                            <label className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                <Upload size={14} /> {returnFile ? 'Replace file' : 'Upload sheet'}
                                                <input
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    className="hidden"
                                                    onChange={(e) => { parseReturnFile(e.target.files?.[0] || null); e.target.value = ''; }}
                                                />
                                            </label>
                                            <button onClick={downloadReturnTemplate}
                                                title="Download the blank sheet to send the client"
                                                className="px-3 py-2 border rounded-md text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap flex items-center gap-1.5">
                                                <Download size={14} /> Template
                                            </button>
                                        </div>
                                        {returnFile && (
                                            <p className="text-[11px] text-gray-500 mt-1 truncate">
                                                {returnFile} — <strong>{returnRows.length}</strong> row(s)
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-end gap-2">
                                        <button onClick={runReturnPreview} disabled={!returnRows.length || !!returnBusy}
                                            className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                                            <Search size={15} /> {returnBusy === 'preview' ? 'Pricing…' : 'Preview'}
                                        </button>
                                        <button onClick={runReturnGenerate}
                                            disabled={!returnPreview?.lines?.length || !!returnBusy}
                                            className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-40">
                                            <FileText size={15} /> {returnBusy === 'generate' ? 'Generating…' : 'Generate'}
                                        </button>
                                    </div>
                                </div>

                                {/* What the parser made of the columns. Stated because a
                                    Status column that was not found silently prices every
                                    row as duty-only, which looks like a working invoice. */}
                                {returnColumns && (
                                    <div className={`rounded-md px-4 py-3 mb-4 text-sm border-l-4 ${
                                        returnColumns.status >= 0
                                            ? 'bg-gray-50 border-gray-300 text-gray-700'
                                            : 'bg-amber-50 border-amber-500 text-amber-900'}`}>
                                        <p className="font-semibold mb-1">Columns read from your sheet</p>
                                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
                                            {[
                                                ['Tracking number', returnColumns.tracking],
                                                ['Status', returnColumns.status],
                                                ['Customs duty', returnColumns.duty],
                                            ].map(([label, idx]: any) => (
                                                <span key={label}>
                                                    {label}:{' '}
                                                    {idx >= 0
                                                        ? <strong>“{returnColumns.headers[idx] || '—'}”</strong>
                                                        : <strong className="text-amber-700">not found</strong>}
                                                </span>
                                            ))}
                                        </div>
                                        {returnColumns.status < 0 && (
                                            <p className="mt-2">
                                                Without a status column nothing can be recognised as a return, so every
                                                row would bill its customs duty and no return charge. Add the column and
                                                upload again.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {!returnRows.length && (
                                    <div className="border border-dashed rounded-lg py-10 text-center">
                                        <CornerUpLeft size={28} className="mx-auto text-gray-300 mb-2" />
                                        <p className="text-sm text-gray-500">Upload the client's return sheet to begin.</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Three columns: Tracking Number, Status, Customs Duty. Rows whose
                                            status says <strong>return</strong> are priced by the client's return rate;
                                            every other status bills its customs duty alone. The weight is taken from
                                            the shipment in the system, so it is not on the sheet.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Return preview ───────────────────────── */}
                    {billMode === 'return' && returnPreview && (() => {
                        const t   = returnPreview.totals;
                        const sym = returnPreview.currency_symbol;
                        const p   = returnPreview.problems;
                        const held = p.not_found.length + p.already_billed.length;

                        return (
                            <div className="bg-white rounded-lg shadow p-5 mb-6 border-l-4 border-gray-800">
                                <h4 className="font-semibold mb-3">
                                    Return preview · {returnPreview.client.name}
                                </h4>

                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                                    {[
                                        ['Billable', t.shipments.toLocaleString(), ''],
                                        ['Returned', t.returns.toLocaleString(), 'priced by the return rate'],
                                        ['Duty only', t.duty_only.toLocaleString(), 'not returned, no return charge'],
                                        ['Return charges', formatMoney(t.return_charges, sym), ''],
                                        ['Customs duty', formatMoney(t.customs_duty, sym), 'passed through'],
                                    ].map(([label, value, hint]: any) => (
                                        <div key={label} className="border rounded-md px-3 py-2">
                                            <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
                                            <p className="text-lg font-semibold text-gray-900">{value}</p>
                                            {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between bg-gray-50 border rounded-md px-4 py-3 mb-4">
                                    <span className="text-sm text-gray-600">
                                        Invoice total <span className="text-gray-400">— no VAT on a return invoice</span>
                                    </span>
                                    <span className="text-xl font-semibold text-[#E04343]">{formatMoney(t.total, sym)}</span>
                                </div>

                                {held > 0 && (
                                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r p-4 mb-4">
                                        <p className="font-semibold text-amber-900 flex items-center gap-2">
                                            <AlertTriangle size={16} /> {held} row(s) held back
                                        </p>
                                        {p.not_found.length > 0 && (
                                            <p className="text-sm text-amber-800 mt-2">
                                                <strong>{p.not_found.length} not in the system.</strong> Only shipments
                                                the system knows can be return billed — otherwise there is nothing to
                                                stop the same sheet billing twice. {p.not_found.slice(0, 8).join(', ')}
                                                {p.not_found.length > 8 && ` and ${p.not_found.length - 8} more`}.
                                            </p>
                                        )}
                                        {p.already_billed.length > 0 && (
                                            <p className="text-sm text-amber-800 mt-2">
                                                <strong>{p.already_billed.length} already return billed.</strong>{' '}
                                                {p.already_billed.slice(0, 5).map((x: any) => `${x.tracking} (${x.invoice})`).join(', ')}
                                                {p.already_billed.length > 5 && ` and ${p.already_billed.length - 5} more`}.
                                                Void that invoice to bill them again.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {p.duplicates.length > 0 && (
                                    <p className="text-sm text-gray-600 mb-3">
                                        {p.duplicates.length} tracking number(s) appeared more than once on the sheet and
                                        were counted once: {p.duplicates.slice(0, 6).join(', ')}.
                                    </p>
                                )}

                                {(p.no_weight || []).length > 0 && (
                                    <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r p-4 mb-4">
                                        <p className="text-sm text-amber-900">
                                            <strong>{p.no_weight.length} returned shipment(s) have no weight in the system.</strong>{' '}
                                            They are billed at whatever a zero weight comes to under the return rate — the
                                            base band on a range card, nothing at all on a per-kg one. Correct the weight on
                                            the shipment and preview again if that is wrong:{' '}
                                            {p.no_weight.slice(0, 6).join(', ')}
                                            {p.no_weight.length > 6 && ` and ${p.no_weight.length - 6} more`}.
                                        </p>
                                    </div>
                                )}

                                {p.foreign.length > 0 && (
                                    <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r p-4 mb-4">
                                        <p className="text-sm text-blue-900">
                                            <strong>{p.foreign.length} shipment(s) do not match {returnPreview.client.name}</strong>{' '}
                                            by sender name or account code. They are still billed — you chose the client
                                            and the sheet — but it usually means a missing account code on the shipment:{' '}
                                            {p.foreign.slice(0, 6).join(', ')}.
                                        </p>
                                    </div>
                                )}

                                <div className="border rounded-lg overflow-x-auto max-h-[28rem] overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                                            <tr>
                                                <th className="px-3 py-2 font-semibold">Tracking</th>
                                                <th className="px-3 py-2 font-semibold text-right">Weight</th>
                                                <th className="px-3 py-2 font-semibold">Status</th>
                                                <th className="px-3 py-2 font-semibold">Rate applied</th>
                                                <th className="px-3 py-2 font-semibold text-right">Customs duty</th>
                                                <th className="px-3 py-2 font-semibold text-right">Return cost</th>
                                                <th className="px-3 py-2 font-semibold text-right">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {returnPreview.lines.map((l: any) => (
                                                <tr key={l.tracking} className={l.is_return ? '' : 'bg-gray-50/50'}>
                                                    <td className="px-3 py-2 font-medium text-gray-800">
                                                        {l.tracking}
                                                        {l.foreign && (
                                                            <span className="ml-1.5 text-[10px] text-blue-600" title="Does not match this client">•</span>
                                                        )}
                                                    </td>
                                                    {/* The shipment's own weight, read from the system. */}
                                                    <td className="px-3 py-2 text-right">
                                                        {Number(l.weight) > 0
                                                            ? Number(l.weight).toFixed(2)
                                                            : <span className="text-amber-600" title="No weight on this shipment">0.00</span>}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <span className={`text-[11px] px-1.5 py-0.5 rounded ${
                                                            l.is_return ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                                            {l.status || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-gray-500">{l.calculation}</td>
                                                    <td className="px-3 py-2 text-right">{formatMoney(l.customs_duty, sym)}</td>
                                                    <td className="px-3 py-2 text-right">
                                                        {l.is_return ? formatMoney(l.return_charge, sym) : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-semibold">{formatMoney(l.line_total, sym)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}

                    {/* ── Preview ──────────────────────────────── */}
                    {billMode === 'lastmile' && preview && (
                        <div className="bg-white rounded-lg shadow p-5 mb-6 border-l-4 border-gray-800">
                            <h4 className="font-semibold mb-3">
                                Preview · {formatDate(preview.period.from)} to {formatDate(preview.period.to)}
                            </h4>

                            {(() => {
                                // The tiles change shape depending on whether this client
                                // has docket-level charges. Showing an "Additional charges"
                                // tile reading zero on a client that has none is noise; on
                                // a client that has them, the split is the whole point.
                                const hasCharges = Number(view.charges_subtotal || 0) > 0;

                                const tiles: any[] = [
                                    ['Shipments', view.total_shipments],
                                    ['Dockets', view.total_dockets ?? 0],
                                    ['Total weight', `${view.total_weight} kg`],
                                ];

                                if (hasCharges) {
                                    tiles.push(['Last-mile', formatMoney(view.lastmile_subtotal, previewSymbol)]);
                                    tiles.push(['Additional charges', formatMoney(view.charges_subtotal, previewSymbol)]);
                                }

                                tiles.push(['Subtotal', formatMoney(view.subtotal, previewSymbol)]);
                                tiles.push([`VAT (${view.vat_rate}%)`, formatMoney(view.vat_amount, previewSymbol)]);
                                tiles.push(['Total', formatMoney(view.total_amount, previewSymbol)]);

                                return (
                                    <div className={`grid grid-cols-2 gap-3 mb-4 ${hasCharges ? 'md:grid-cols-4' : 'md:grid-cols-3 lg:grid-cols-6'}`}>
                                        {tiles.map(([label, value], i) => {
                                            const isTotal = i === tiles.length - 1;
                                            return (
                                                <div key={i} className={`rounded p-3 ${isTotal ? 'bg-red-50' : 'bg-gray-50'}`}>
                                                    <p className="text-[11px] text-gray-500">{label}</p>
                                                    <p className={`text-lg font-semibold ${isTotal ? 'text-[#E04343]' : 'text-gray-800'}`}>{value}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}

                            {/* What is NOT being billed, and why. */}
                            {preview.zones_without_rate?.length > 0 && (
                                <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-3 rounded-r text-sm">
                                    <p className="font-semibold text-red-900 flex items-center gap-2">
                                        <AlertTriangle size={15} /> No rate set for {preview.zones_without_rate.join(', ')}
                                    </p>
                                    <p className="text-red-800 mt-1">
                                        Set these under Billing Zones &amp; Rates — generation is blocked until you do.
                                    </p>
                                </div>
                            )}

                            {preview.unmapped_count > 0 && (
                                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-3 rounded-r text-sm">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <p className="font-semibold text-amber-900 flex items-center gap-2">
                                            <AlertTriangle size={15} />
                                            {preview.unmapped_count} shipment(s) could not be priced — destination state not in any zone
                                        </p>
                                        <button onClick={openFixStates}
                                            className="bg-amber-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:bg-amber-700 whitespace-nowrap">
                                            <Pencil size={14} /> Fix these states
                                        </button>
                                    </div>
                                    <p className="text-amber-800 mt-1 mb-2">
                                        These are excluded from the invoice. Correct the state here, or add it to a
                                        zone under Billing Zones &amp; Rates.
                                    </p>
                                    <table className="w-full text-xs">
                                        <thead className="text-amber-900/70">
                                            <tr><th className="text-left py-1">Destination state</th><th className="text-left">Shipments</th><th className="text-left">Why</th><th className="text-left">Examples</th></tr>
                                        </thead>
                                        <tbody>
                                            {preview.unmapped_states.map((u: any) => (
                                                <tr key={u.state} className="border-t border-amber-200">
                                                    <td className="py-1 font-medium">{u.state}</td>
                                                    <td>{u.count}</td>
                                                    <td className="text-amber-800">
                                                        {/* A town sitting in two states is left alone on purpose —
                                                            guessing would put the shipment in the wrong zone. */}
                                                        {u.ambiguous
                                                            ? <>Could be <strong>{(u.options || []).join(' or ')}</strong> — needs a decision</>
                                                            : 'Not in any zone'}
                                                    </td>
                                                    <td className="text-amber-800">{(u.samples || []).join(', ')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Where each priced line got its state. Anything not read
                                straight off the shipment was inferred, so it is called
                                out separately rather than folded into the total. */}
                            {preview.resolved_from?.length > 0 && (
                                <div className={`p-3 mb-3 rounded-r border-l-4 text-sm ${preview.inferred_count > 0 ? 'bg-blue-50 border-blue-500' : 'bg-gray-50 border-gray-300'}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="font-semibold text-gray-800 flex items-center gap-2">
                                            <Info size={15} />
                                            {preview.inferred_count > 0
                                                ? `${preview.inferred_count} shipment(s) priced from an inferred state`
                                                : 'Every shipment priced from its own destination state'}
                                        </p>
                                        {preview.inferred_count > 0 && preview.inferred_lines?.length > 0 && (
                                            <button
                                                onClick={() => setInferred(preview.inferred_lines)}
                                                className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-700"
                                            >
                                                <Eye size={14} /> Review these {preview.inferred_count}
                                            </button>
                                        )}
                                    </div>
                                    {preview.inferred_count > 0 && (
                                        <p className="text-gray-600 mt-1 mb-2 text-xs">
                                            The destination state was missing or held a town name, so it was worked out from
                                            the city or the branch. Worth spot-checking before you generate.
                                        </p>
                                    )}
                                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-700 mt-1">
                                        {preview.resolved_from.map((r: any) => (
                                            <span key={r.key}>
                                                {r.label}: <strong>{r.count}</strong>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* A docket number is required to bill: every charge above
                                last-mile is priced per docket, so a shipment without one
                                cannot be priced completely. Stated loudly rather than
                                folded into the counts below — it is revenue sitting
                                outside this invoice, and it is invisible on the shipment. */}
                            {preview.no_docket > 0 && (
                                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-3 rounded-r text-sm">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <p className="font-semibold text-amber-900 flex items-center gap-2">
                                            <AlertTriangle size={15} />
                                            {preview.no_docket} shipment(s) held back — no docket number
                                        </p>
                                        <button onClick={openNoDocket}
                                            className="bg-amber-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 hover:bg-amber-700 whitespace-nowrap">
                                            <Eye size={14} /> Check these {preview.no_docket}
                                        </button>
                                    </div>
                                    <p className="text-amber-800 mt-1">
                                        These are in the period and otherwise billable, but a docket number is
                                        required: clearance and every other additional charge is priced per docket.
                                        Add their docket numbers and re-run this preview to bill them.
                                    </p>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-4 text-xs text-gray-600 mb-4">
                                <span>Already invoiced (skipped): <strong>{preview.already_invoiced}</strong></span>
                                <span>Excluded by your filters: <strong>{preview.excluded_by_filter}</strong></span>
                                <span>No docket number: <strong>{preview.no_docket ?? 0}</strong></span>
                            <span className={requireDocket ? '' : 'text-amber-700'}>
                                {requireDocket
                                    ? 'Docket required'
                                    : 'Billing without dockets — last-mile only on those'}
                            </span>
                            </div>


                            {/* ── Per-docket breakdown ──────────────────────────
                                The invoice is two layers: last-mile per shipment, and
                                charges per docket. This is where the second layer is
                                checked before generating — every docket the invoice
                                touches, its weight, and what each charge did to it.

                                Rendered whether or not there are dockets: an absent
                                section cannot be told apart from a broken one, and
                                "which dockets am I about to bill" is the question this
                                screen is now opened to answer. */}
                            {!view.by_docket?.length ? (
                                <div className="border rounded-lg px-4 py-6 text-center mb-4 mt-5">
                                    <p className="text-sm text-gray-500 font-medium">No dockets in this selection</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {preview.no_docket > 0
                                            ? `${preview.no_docket} shipment(s) here carry no docket number, so none of them can be billed.`
                                            : preview.already_invoiced > 0
                                                ? `All ${preview.already_invoiced} shipment(s) in this period are already on an invoice.`
                                                : 'Nothing billable in this period — widen the dates or clear the filters.'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-baseline justify-between mb-2 mt-5 flex-wrap gap-2">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <p className="text-xs font-semibold text-gray-600">
                                                By docket ({view.by_docket.length})
                                            </p>

                                            {/* Saved weights. Deliberately not called a draft: a draft
                                                invoice already exists in this screen and claims its
                                                shipments, which is the one thing these must not do. */}
                                            <button
                                                type="button"
                                                onClick={() => { setSheetName(''); setSaveSheetOpen(true); }}
                                                className="text-[11px] rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
                                            >
                                                Save weights
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setSheetsOpen(true); loadSheets(); }}
                                                className="text-[11px] rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
                                            >
                                                Load saved weights{sheets.length > 0 ? ` (${sheets.length})` : ''}
                                            </button>
                                        </div>
                                        {view.charge_totals?.length > 0 && (
                                            <p className="text-[11px] text-gray-500">
                                                {view.charge_totals.map((c: any) => (
                                                    <span key={c.name} className="ml-3">
                                                        {c.name}:{' '}
                                                        <strong className="text-gray-800">
                                                            {formatMoney(c.amount, previewSymbol)}
                                                        </strong>
                                                        <span className="text-gray-400">
                                                            {' '}({c.dockets} docket{c.dockets === 1 ? '' : 's'}
                                                            {c.type === 'flat' ? ` × ${formatMoney(c.rate, previewSymbol)}` : ''})
                                                        </span>
                                                    </span>
                                                ))}
                                            </p>
                                        )}
                                    </div>

                                    {sheetApplyNote && (
                                        <div className="mb-2 flex items-start justify-between gap-3 rounded-r border-l-4 border-blue-500 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                                            <span>{sheetApplyNote}</span>
                                            <button
                                                type="button"
                                                onClick={() => setSheetApplyNote('')}
                                                className="shrink-0 text-blue-500 hover:text-blue-800"
                                            >
                                                Dismiss
                                            </button>
                                        </div>
                                    )}

                                    {view.has_overrides && (
                                        <div className="mb-2 rounded-r border-l-4 border-[#E04343] bg-red-50 px-3 py-2 text-xs text-[#B33232]">
                                            The totals above and below include your edited docket weights. Only per-kg
                                            charges change — last-mile and the invoice's total weight still come from the
                                            shipments themselves. Generation re-checks these figures on the server.
                                        </div>
                                    )}

                                    <div className="overflow-x-auto mb-4 border rounded-lg">
                                        <table className="w-full text-sm">
                                            <thead className="text-[11px] text-[#808080] bg-gray-50">
                                                <tr>
                                                    <th className="text-left py-2 px-3">Docket</th>
                                                    <th className="text-right px-3">Shipments</th>
                                                    <th className="text-right px-3">
                                                        Weight (kg)
                                                        <span className="block font-normal text-[10px] text-gray-400 normal-case">
                                                            editable — bills per-kg charges
                                                        </span>
                                                    </th>
                                                    <th className="text-right px-3">Last-mile</th>
                                                    {/* One column per configured charge, so a docket
                                                        reads across as a single row rather than
                                                        repeating once per charge. */}
                                                    {(view.charge_totals || []).map((c: any) => (
                                                        <th key={c.name} className="text-right px-3">{c.name}</th>
                                                    ))}
                                                    <th className="text-right py-2 px-3">Docket total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {view.by_docket.map((d: any) => (
                                                    <tr key={d.docket} className="border-t">
                                                        <td className="py-1.5 px-3 font-medium">{d.docket}</td>
                                                        <td className="text-right px-3">{d.shipments}</td>
                                                        <td className="px-3 py-1.5">
                                                            {/* The weight per-kg charges bill on. Defaults to the sum of
                                                                the docket's shipment weights, which is all the system can
                                                                infer; a consignment weighed as a whole at clearing
                                                                routinely differs. Last-mile is unaffected — that is priced
                                                                per shipment from each shipment's own weight. */}
                                                            <div className="flex flex-col items-end">
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={docketWeights[d.docket] ?? String(d.weight)}
                                                                    onChange={(e) => setDocketWeights({ ...docketWeights, [d.docket]: e.target.value })}
                                                                    className={`w-24 rounded border px-2 py-1 text-right text-sm outline-none focus:ring-[3px] focus:ring-[#E04343]/10 ${d.weight_overridden
                                                                        ? 'border-[#E04343] bg-red-50 font-semibold text-[#B33232]'
                                                                        : 'border-gray-200 focus:border-[#E04343]'}`}
                                                                />

                                                                {d.weight_overridden && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const next = { ...docketWeights };
                                                                            delete next[d.docket];
                                                                            setDocketWeights(next);
                                                                        }}
                                                                        className="mt-0.5 text-[10px] text-gray-400 underline-offset-2 hover:text-[#E04343] hover:underline"
                                                                    >
                                                                        shipments total {d.weight} kg — reset
                                                                    </button>
                                                                )}

                                                                {/* A docket often bills across two invoices. Typing the
                                                                    full consignment weight on both would charge per-kg on
                                                                    twice the goods, so what has already been charged is
                                                                    stated here. Nothing is blocked — it is the operator's
                                                                    call. */}
                                                                {Number(d.previously_charged_weight) > 0 && (
                                                                    <span
                                                                        className="mt-0.5 text-[10px] text-amber-700"
                                                                        title={`Already charged on ${(d.previously_charged_invoices || []).join(', ')}`}
                                                                    >
                                                                        {Number(d.previously_charged_weight).toLocaleString()} kg already charged
                                                                        {(d.previously_charged_invoices || []).length === 1
                                                                            ? ` on ${d.previously_charged_invoices[0]}`
                                                                            : ''}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-right px-3">{formatMoney(d.lastmile, previewSymbol)}</td>
                                                        {(view.charge_totals || []).map((c: any) => {
                                                            const hit = (d.charges || []).find((x: any) => x.name === c.name);
                                                            return (
                                                                <td key={c.name} className="text-right px-3">
                                                                    {hit ? (
                                                                        <>
                                                                            {formatMoney(hit.amount, previewSymbol)}
                                                                            <span className="block text-[10px] text-gray-400">
                                                                                {hit.calculation}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        // A flat charge already taken on this docket by an
                                                                        // earlier invoice. Said out loud, because a blank
                                                                        // cell reads as a mistake.
                                                                        <span className="text-[10px] text-gray-400" title="Already charged on an earlier invoice">
                                                                            already charged
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                        <td className="text-right px-3 font-semibold">
                                                            {formatMoney(d.total, previewSymbol)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr className="border-t-2 bg-gray-50 font-semibold">
                                                    <td className="py-2 px-3">
                                                        {view.by_docket.length} docket{view.by_docket.length === 1 ? '' : 's'}
                                                    </td>
                                                    <td className="text-right px-3">{view.total_shipments}</td>
                                                    <td className="text-right px-3">
                                                        {view.total_weight} kg
                                                        {view.has_overrides && (
                                                            <span className="block text-[10px] font-normal text-[#B33232]">
                                                                charging on{' '}
                                                                {view.by_docket
                                                                    .reduce((sum: number, r: any) => sum + Number(r.charge_weight), 0)
                                                                    .toLocaleString()} kg
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-right px-3">
                                                        {formatMoney(view.lastmile_subtotal, previewSymbol)}
                                                    </td>
                                                    {(view.charge_totals || []).map((c: any) => (
                                                        <td key={c.name} className="text-right px-3">
                                                            {formatMoney(c.amount, previewSymbol)}
                                                        </td>
                                                    ))}
                                                    <td className="text-right px-3 text-[#E04343]">
                                                        {formatMoney(view.subtotal, previewSymbol)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* Zones remain useful for reconciling last-mile against the rate
                                cards, but they are secondary now: the docket is the unit an
                                invoice is actually assembled and checked by. */}
                            {preview.by_zone?.length > 0 && (
                                <>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">By zone</p>
                                    <div className="overflow-x-auto mb-4">
                                        <table className="w-full text-sm">
                                            <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                <tr><th className="text-left py-1">Zone</th><th className="text-left">Shipments</th><th className="text-left">Weight</th><th className="text-right">Amount</th></tr>
                                            </thead>
                                            <tbody>
                                                {preview.by_zone.map((z: any) => (
                                                    <tr key={z.zone} className="border-t">
                                                        <td className="py-1.5 font-medium">{z.zone}</td>
                                                        <td>{z.shipments}</td>
                                                        <td>{z.weight} kg</td>
                                                        <td className="text-right">{formatMoney(z.amount, previewSymbol)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {preview.sample_lines?.length > 0 && (
                                <>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">
                                        Sample lines (first {preview.sample_lines.length}) — check the pricing before generating
                                    </p>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                <tr>
                                                    <th className="text-left py-1">Shipment</th><th className="text-left">State</th>
                                                    <th className="text-left">Zone</th><th className="text-left">Weight</th>
                                                    <th className="text-left">Rate applied</th><th className="text-right">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preview.sample_lines.map((l: any) => (
                                                    <tr key={l.shipment_id} className="border-t">
                                                        <td className="py-1.5">{l.shipment_number}</td>
                                                        <td>
                                                            {l.receiver_state}
                                                            {l.resolved_from && l.resolved_from !== 'state' && (
                                                                <span className="ml-1 text-[10px] text-blue-600" title={`Worked out from: ${l.stated_state || '(blank)'}`}>
                                                                    inferred
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{l.zone_name}</td>
                                                        <td>{l.weight} kg</td>
                                                        <td className="text-gray-500">{l.rate_description}</td>
                                                        <td className="text-right">{formatMoney(l.line_total, previewSymbol)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ── Invoice list ─────────────────────────── */}
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                        <h3 className="text-[17px] font-semibold text-[#1C1C1C]">Generated invoices</h3>
                        <div className="flex gap-2">
                            <input value={search} onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && loadInvoices()}
                                placeholder="Invoice number…" className="px-3 py-2 border rounded-md text-sm" />
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-3 py-2 border rounded-md text-sm bg-white">
                                <option value="">All statuses</option>
                                <option value="finalised">Finalised</option>
                                <option value="sent">Sent</option>
                                <option value="paid">Paid</option>
                                <option value="draft">Draft (unfinished)</option>
                                <option value="void">Void</option>
                            </select>
                            <button onClick={loadInvoices} className="bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm">
                                <RefreshCw size={15} />
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-600 border-separate border-spacing-y-2">
                            <thead className="text-[12px] text-[#808080]">
                                <tr>
                                    <th className="px-3 py-2">Invoice</th>
                                    <th className="px-3">Period</th>
                                    <th className="px-3">Shipments</th>
                                    <th className="px-3">Weight</th>
                                    <th className="px-3">Total</th>
                                    <th className="px-3">Status</th>
                                    <th className="px-3">Generated</th>
                                    <th className="px-3">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listLoader ? (
                                    <tr><td colSpan={8} className="text-center py-8">
                                        <div className="flex justify-center"><Oval height={34} width={34} color="#E04343" secondaryColor="#FCBF94" /></div>
                                    </td></tr>
                                ) : invoices.length === 0 ? (
                                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No invoices yet.</td></tr>
                                ) : invoices.map((inv: any) => (
                                    <tr key={inv.id} className="bg-white">
                                        <td className="px-3 py-2.5 font-medium text-gray-800">
                                            {inv.invoice_number}
                                            {/* Two streams in one list, so which one this is has to be
                                                on the row — the totals mean different things. */}
                                            {inv.bill_type === 'return' && (
                                                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 align-middle">
                                                    Returns
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3">{formatDate(inv.period_start)} – {formatDate(inv.period_end)}</td>
                                        <td className="px-3">{inv.total_shipments}</td>
                                        <td className="px-3">{inv.total_weight} kg</td>
                                        <td className="px-3 font-semibold text-gray-800">{formatMoney(inv.total_amount, symbolFor(inv.currency))}</td>
                                        <td className="px-3">
                                            <span className={`px-2 py-0.5 rounded-full border text-[11px] capitalize ${statusChip(inv.status)}`}>
                                                {inv.status === 'draft' ? 'Draft — unfinished' : inv.status}
                                            </span>
                                        </td>
                                        <td className="px-3 text-xs text-gray-500">{formatDate(inv.created_at)}</td>
                                        <td className="px-3">
                                            <div className="flex gap-1.5">
                                                <button onClick={() => openDetail(inv)} title="View" className="text-gray-500 hover:text-gray-800"><Eye size={16} /></button>

                                                {/* A draft has no totals and no charges yet, so exporting one would
                                                    hand someone a document that understates the bill. Discard is the
                                                    only sensible action on it. */}
                                                {inv.status === 'draft' ? (
                                                    <button
                                                        onClick={() => discardDraft(inv)}
                                                        title="Discard this unfinished draft and release its shipments"
                                                        className="rounded bg-[#E04343] px-2 py-0.5 text-[11px] font-medium text-white hover:bg-[#c93a3a]"
                                                    >
                                                        Discard
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button onClick={() => download(inv, 'xlsx')} disabled={busy === `xlsx-${inv.id}`} title="Excel" className="text-green-600 hover:text-green-800 disabled:opacity-40"><Download size={16} /></button>
                                                        <button onClick={() => download(inv, 'pdf')} disabled={busy === `pdf-${inv.id}`} title="PDF" className="text-red-600 hover:text-red-800 disabled:opacity-40"><FileText size={16} /></button>
                                                        {inv.status !== 'void' && (
                                                            <button onClick={() => voidInvoice(inv)} disabled={busy === `void-${inv.id}`} title="Void" className="text-gray-400 hover:text-red-600 disabled:opacity-40"><Ban size={16} /></button>
                                                        )}
                                                        {/* Only on a void invoice, and only for an administrator.
                                                            Both are enforced server-side; this keeps a button that
                                                            would only be refused off everyone else's screen. */}
                                                        {inv.status === 'void' && isAdmin && (
                                                            <button
                                                                onClick={() => deleteInvoice(inv)}
                                                                disabled={busy === `delete-${inv.id}`}
                                                                title="Delete this voided invoice permanently"
                                                                className="text-gray-400 hover:text-[#E04343] disabled:opacity-40"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ── Docket required? ─────────────────────────────
                Asked before the preview because it changes what is billed: without
                a docket a shipment can only take the last-mile rate, since clearance
                and every other additional charge is priced per docket. */}
            {askDocket && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="px-5 py-4 border-b">
                            <h3 className="font-semibold">Bill shipments without a docket number?</h3>
                            <p className="text-xs text-gray-500 mt-1">
                                This changes what goes on the invoice, so it is worth a moment.
                            </p>
                        </div>

                        <div className="p-5 flex flex-col gap-3">
                            <button
                                onClick={() => { setRequireDocket(true); setAskDocket(false); runPreview(true); }}
                                className="text-left border rounded-lg p-3 hover:border-gray-400 transition">
                                <p className="font-semibold text-sm text-gray-800">
                                    No — a docket is required <span className="font-normal text-gray-400">(recommended)</span>
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Shipments with no docket are held back and listed, so you can add their
                                    numbers and bill them properly with their clearance charges.
                                </p>
                            </button>

                            <button
                                onClick={() => { setRequireDocket(false); setAskDocket(false); runPreview(false); }}
                                className="text-left border rounded-lg p-3 hover:border-gray-400 transition">
                                <p className="font-semibold text-sm text-gray-800">Yes — bill them anyway</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    They are included at the <strong>last-mile rate only</strong>. Clearance and
                                    other additional charges are priced per docket, so a shipment without one
                                    cannot carry them.
                                </p>
                            </button>
                        </div>

                        <div className="flex justify-end px-5 py-3 border-t">
                            <button onClick={() => setAskDocket(false)}
                                className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Correct unmapped destination states ──────────
                One decision per distinct state value, not per shipment: the whole
                point is that "Lekki Phase 2" is one mistake made many times. */}
            {fixStates && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">Fix destination states</h3>
                                <p className="text-xs text-gray-500">
                                    Set one state for a whole value, or open it and set each shipment on its own.
                                    Either way the shipment itself is updated, not just this invoice.
                                </p>
                            </div>
                            <button onClick={closeFixStates} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-3 overflow-y-auto">
                            <div className="space-y-2">
                                {fixStates.map((u: any) => {
                                    const chosen = stateChoices[u.state] || '';
                                    const picked = setIndividually(u.state);
                                    const open   = expandedState === u.state;
                                    const rows   = stateRows[u.state] || [];
                                    const query = (stateSearch[u.state] || '').toLowerCase();
                                    const options = allStates.filter((st) => st.toLowerCase().includes(query));

                                    return (
                                        <div key={u.state} className="border rounded-md p-3">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <div className="flex-1 min-w-[180px]">
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {u.state === '(blank)'
                                                            ? <span className="italic text-gray-500">(no state on the shipment)</span>
                                                            : u.state}
                                                    </p>
                                                    <p className="text-[11px] text-gray-500">
                                                        {u.count} shipment(s)
                                                        {u.ambiguous && <span className="text-amber-700"> · could be {(u.options || []).join(' or ')}</span>}
                                                    </p>
                                                </div>

                                                <div className="relative w-56">
                                                    <button type="button"
                                                        disabled={picked > 0}
                                                        title={picked > 0
                                                            ? 'Cleared because shipments in this group have been set individually'
                                                            : `Set all ${u.count} shipment(s) to one state`}
                                                        onClick={() => setOpenStateRow(openStateRow === u.state ? null : u.state)}
                                                        className={`w-full px-3 py-2 border rounded-md text-sm text-left flex items-center justify-between gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
                                                            chosen ? 'border-green-400 bg-green-50 text-green-900' : 'bg-white'}`}>
                                                        <span className="truncate">{chosen || 'Set all to…'}</span>
                                                        <ChevronDown size={14} className="text-gray-400 shrink-0" />
                                                    </button>

                                                    {openStateRow === u.state && (
                                                        <div className="absolute z-30 mt-1 w-full bg-white border rounded-md shadow-lg">
                                                            <div className="p-2 border-b">
                                                                <input autoFocus
                                                                    value={stateSearch[u.state] || ''}
                                                                    onChange={(e) => setStateSearch({ ...stateSearch, [u.state]: e.target.value })}
                                                                    placeholder="Search states…"
                                                                    className="w-full px-2 py-1.5 border rounded text-xs" />
                                                            </div>
                                                            <div className="max-h-48 overflow-y-auto">
                                                                {options.length === 0 ? (
                                                                    <p className="px-3 py-2 text-xs text-gray-400">No state matches that.</p>
                                                                ) : options.map((st) => (
                                                                    <button key={st} type="button"
                                                                        onClick={() => {
                                                                            setStateChoices({ ...stateChoices, [u.state]: st });
                                                                            // The two are mutually exclusive per group, so
                                                                            // there is never a question of which one won.
                                                                            setShipmentChoices((prev) => {
                                                                                const next = { ...prev };
                                                                                (stateRows[u.state] || []).forEach((r: any) => delete next[r.id]);
                                                                                return next;
                                                                            });
                                                                            setOpenStateRow(null);
                                                                        }}
                                                                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50">
                                                                        {st}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-3 mt-2">
                                                <button type="button"
                                                    onClick={() => toggleStateGroup(u.state)}
                                                    className="text-[12px] text-blue-700 hover:text-blue-900 flex items-center gap-1">
                                                    <ChevronDown size={13} className={open ? 'rotate-180 transition' : 'transition'} />
                                                    {open ? 'Hide the shipments' : `Set these ${u.count} one by one`}
                                                </button>

                                                {picked > 0 && (
                                                    <span className="text-[11px] text-green-700">
                                                        {picked} of {rows.length} set individually
                                                    </span>
                                                )}

                                                {!open && (u.samples || []).length > 0 && (
                                                    <span className="text-[11px] text-gray-400 break-words">
                                                        e.g. {(u.samples || []).slice(0, 4).join(', ')}
                                                    </span>
                                                )}
                                            </div>

                                            {open && (
                                                <div className="mt-3 border-t pt-3">
                                                    {stateRowsLoading === u.state ? (
                                                        <p className="text-xs text-gray-400 py-4 text-center">Loading the shipments…</p>
                                                    ) : rows.length === 0 ? (
                                                        <p className="text-xs text-gray-400 py-4 text-center">
                                                            No shipments carry this value any more — it may have been corrected already.
                                                        </p>
                                                    ) : (
                                                        <>
                                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                                <p className="text-[11px] text-gray-500">
                                                                    The city and address are usually what says where it was really going.
                                                                </p>
                                                                {picked > 0 && (
                                                                    <button type="button"
                                                                        onClick={() => setShipmentChoices((prev) => {
                                                                            const next = { ...prev };
                                                                            rows.forEach((r: any) => delete next[r.id]);
                                                                            return next;
                                                                        })}
                                                                        className="text-[11px] text-gray-500 hover:text-gray-800 underline">
                                                                        Clear these
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <div className="border rounded-md overflow-x-auto max-h-72 overflow-y-auto">
                                                                <table className="w-full text-xs">
                                                                    <thead className="bg-gray-50 text-left text-[10px] uppercase text-gray-500">
                                                                        <tr>
                                                                            <th className="px-2 py-1.5 font-semibold">Shipment</th>
                                                                            <th className="px-2 py-1.5 font-semibold">Receiver</th>
                                                                            <th className="px-2 py-1.5 font-semibold">City</th>
                                                                            <th className="px-2 py-1.5 font-semibold">Address</th>
                                                                            <th className="px-2 py-1.5 font-semibold w-48">Real state</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y">
                                                                        {rows.map((r: any) => (
                                                                            <tr key={r.id} className={shipmentChoices[r.id] ? 'bg-green-50/60' : ''}>
                                                                                <td className="px-2 py-1.5 font-medium text-gray-800 whitespace-nowrap">
                                                                                    {r.ShipmentNumber}
                                                                                </td>
                                                                                <td className="px-2 py-1.5 text-gray-600">
                                                                                    {r.ReceiverName && r.ReceiverName !== 'empty' ? r.ReceiverName : '—'}
                                                                                </td>
                                                                                <td className="px-2 py-1.5 text-gray-600">
                                                                                    {r.ReceiverAddCity && r.ReceiverAddCity !== 'empty' ? r.ReceiverAddCity : '—'}
                                                                                </td>
                                                                                <td className="px-2 py-1.5 text-gray-500 max-w-[16rem] truncate"
                                                                                    title={r.ReceiverAdd1 || ''}>
                                                                                    {r.ReceiverAdd1 && r.ReceiverAdd1 !== 'empty' ? r.ReceiverAdd1 : '—'}
                                                                                </td>
                                                                                <td className="px-2 py-1.5">
                                                                                    {/* A native select on purpose: this list scrolls
                                                                                        inside the modal, and a floating panel would be
                                                                                        clipped by it. Typing still jumps to a state. */}
                                                                                    <select
                                                                                        value={shipmentChoices[r.id] || ''}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            setShipmentChoices((prev) => {
                                                                                                const next = { ...prev };
                                                                                                if (v) next[r.id] = v; else delete next[r.id];
                                                                                                return next;
                                                                                            });
                                                                                            // Individual picks and a whole-group
                                                                                            // mapping cannot both stand.
                                                                                            if (v && stateChoices[u.state]) {
                                                                                                setStateChoices((prev) => {
                                                                                                    const next = { ...prev };
                                                                                                    delete next[u.state];
                                                                                                    return next;
                                                                                                });
                                                                                            }
                                                                                        }}
                                                                                        className={`w-full px-2 py-1.5 border rounded text-xs ${
                                                                                            shipmentChoices[r.id] ? 'border-green-400 bg-green-50 text-green-900' : 'bg-white'}`}
                                                                                    >
                                                                                        <option value="">Choose a state…</option>
                                                                                        {allStates.map((st) => (
                                                                                            <option key={st} value={st}>{st}</option>
                                                                                        ))}
                                                                                    </select>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t">
                            <p className="text-xs text-gray-500">
                                {Object.values(stateChoices).filter(Boolean).length} of {fixStates.length} value(s) set
                                as a whole
                                {Object.values(shipmentChoices).filter(Boolean).length > 0 &&
                                    `, ${Object.values(shipmentChoices).filter(Boolean).length} shipment(s) set individually`}
                                . Anything left blank is not touched.
                            </p>
                            <div className="flex gap-2">
                                <button onClick={closeFixStates}
                                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                                <button onClick={applyStateFixes} disabled={fixing}
                                    className="bg-[#E04343] text-white px-4 py-2 rounded text-sm disabled:opacity-60">
                                    {fixing ? 'Updating…' : 'Update and re-check'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Held back for want of a docket ───────────────
                Shows the docket column as it actually is, so "no docket" can be
                verified rather than taken on trust. */}
            {noDocket && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">Shipments held back — no docket number</h3>
                                <p className="text-xs text-gray-500">
                                    In the period and otherwise billable. Check the docket column, then add the
                                    numbers under Shipments → Restore docket numbers and preview again.
                                </p>
                            </div>
                            <button onClick={() => setNoDocket(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-3 min-h-0 flex-1 flex flex-col">
                            <div className="overflow-auto flex-1 min-h-0 border rounded">
                                <table className="w-full text-xs">
                                    <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                        <tr>
                                            <th className="text-left font-semibold px-2 py-2">Shipment</th>
                                            <th className="text-left font-semibold px-2 py-2">Date</th>
                                            <th className="text-left font-semibold px-2 py-2">Receiver</th>
                                            <th className="text-left font-semibold px-2 py-2">State</th>
                                            <th className="text-left font-semibold px-2 py-2">Weight</th>
                                            <th className="text-left font-semibold px-2 py-2">Docket</th>
                                            <th className="text-left font-semibold px-2 py-2">Manifest</th>
                                            <th className="text-left font-semibold px-2 py-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {noDocketLoading ? (
                                            <tr><td colSpan={8} className="text-center py-8">
                                                <div className="flex justify-center"><Oval height={30} width={30} color="#E04343" secondaryColor="#FCBF94" /></div>
                                            </td></tr>
                                        ) : (noDocket.data || []).length === 0 ? (
                                            <tr><td colSpan={8} className="text-center py-8 text-gray-400">Nothing to show.</td></tr>
                                        ) : (noDocket.data || []).map((r: any) => (
                                            <tr key={r.id} className="border-t">
                                                <td className="px-2 py-1.5 font-medium text-gray-800">{r.ShipmentNumber}</td>
                                                <td className="px-2 whitespace-nowrap">{formatDate(r.created_at)}</td>
                                                <td className="px-2">{r.ReceiverName}</td>
                                                <td className="px-2">{r.ReceiverAddState || <span className="text-gray-400 italic">(blank)</span>}</td>
                                                <td className="px-2 whitespace-nowrap">{Number(r.Weight)} kg</td>
                                                <td className="px-2">
                                                    {/* The point of the modal: prove it really is empty. */}
                                                    <span className="text-amber-700 italic">
                                                        {r.docket_number === null ? '(none)' : r.docket_number === '' ? '(empty)' : r.docket_number}
                                                    </span>
                                                </td>
                                                <td className="px-2 text-gray-500">{r.manifest_name || r.manifest_id || '—'}</td>
                                                <td className="px-2 text-gray-500">{r.Status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {noDocket.total > (noDocket.data || []).length && (
                                <p className="text-xs text-amber-700 mt-3 shrink-0">
                                    Showing the first {(noDocket.data || []).length} of {noDocket.total}.
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end px-5 py-3 border-t">
                            <button onClick={() => setNoDocket(null)} className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Inferred-state review ────────────────────────
                The shipments whose destination state was worked out rather than read.
                Shown side by side — what the shipment says vs what it was priced as —
                so a wrong inference is obvious before the invoice is committed. */}
            {inferred && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">Shipments priced from an inferred state</h3>
                                <p className="text-xs text-gray-500">
                                    The destination state was blank or held a town name. Check the pricing looks right —
                                    or fix the state on the shipment and preview again.
                                </p>
                            </div>
                            <button onClick={() => setInferred(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-3 min-h-0 flex-1 flex flex-col">
                            {/* The header is deliberately not sticky. Inside a nested flex
                                scroll container it resolved against the viewport instead
                                and floated out over the page — a header that scrolls with
                                its rows is worth more than one that occasionally escapes. */}
                            <div className="overflow-auto flex-1 min-h-0 border rounded">
                                <table className="w-full text-xs">
                                    <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                        <tr>
                                            <th className="text-left font-semibold px-2 py-2">Shipment</th>
                                            <th className="text-left font-semibold px-2 py-2">Receiver</th>
                                            <th className="text-left font-semibold px-2 py-2">Shipment says</th>
                                            <th className="text-left font-semibold px-2 py-2">City</th>
                                            <th className="text-left font-semibold px-2 py-2">Priced as</th>
                                            <th className="text-left font-semibold px-2 py-2">Worked out from</th>
                                            <th className="text-left font-semibold px-2 py-2">Zone</th>
                                            <th className="text-left font-semibold px-2 py-2">Weight</th>
                                            <th className="text-right font-semibold px-2 py-2">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inferred.map((l: any) => (
                                            <tr key={l.shipment_id} className="border-t">
                                                <td className="px-2 py-1.5 font-medium text-gray-800">{l.shipment_number}</td>
                                                <td className="px-2">{l.receiver_name}</td>
                                                <td className={`px-2 ${l.stated_state ? '' : 'text-gray-400 italic'}`}>
                                                    {l.stated_state || '(blank)'}
                                                </td>
                                                <td className="px-2">{l.receiver_city || '—'}</td>
                                                <td className="px-2 font-semibold text-blue-700">{l.receiver_state}</td>
                                                <td className="px-2 text-gray-500">
                                                    {l.resolved_from === 'state_as_city' ? 'City given as the state'
                                                        : l.resolved_from === 'city' ? 'Destination city'
                                                        : l.resolved_from === 'branch' ? 'Destination branch'
                                                        : l.resolved_from === 'state_to' ? 'Linked state record'
                                                        : l.resolved_from}
                                                </td>
                                                <td className="px-2">{l.zone_name}</td>
                                                <td className="px-2 whitespace-nowrap">{l.weight} kg</td>
                                                <td className="px-2 text-right whitespace-nowrap">{formatMoney(l.line_total, previewSymbol)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {preview?.inferred_count > inferred.length && (
                                <p className="text-xs text-amber-700 mt-3 shrink-0">
                                    Showing the first {inferred.length} of {preview.inferred_count}.
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end px-5 py-3 border-t">
                            <button onClick={() => setInferred(null)} className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Detail drawer ────────────────────────────── */}
            {detail && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[88vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">{detail.invoice?.invoice_number}</h3>
                                <p className="text-xs text-gray-500">
                                    {formatDate(detail.invoice?.period_start)} – {formatDate(detail.invoice?.period_end)}
                                    {detail.invoice?.status === 'void' && <span className="ml-2 text-red-600 font-semibold">VOID</span>}
                                </p>
                            </div>
                            <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-3 overflow-y-auto">
                            {detailLoader ? (
                                <div className="flex justify-center py-10"><Oval height={40} width={40} color="#E04343" secondaryColor="#FCBF94" /></div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                        {[
                                            ['Shipments', detail.invoice?.total_shipments],
                                            // Only split the subtotal when there is something to split.
                                            ...(Number(detail.invoice?.charges_subtotal || 0) > 0
                                                ? [
                                                    ['Last-mile', formatMoney(detail.invoice?.lastmile_subtotal, detailSymbol)],
                                                    ['Additional charges', formatMoney(detail.invoice?.charges_subtotal, detailSymbol)],
                                                ]
                                                : []),
                                            ['Subtotal', formatMoney(detail.invoice?.subtotal, detailSymbol)],
                                            [`VAT (${detail.invoice?.vat_rate}%)`, formatMoney(detail.invoice?.vat_amount, detailSymbol)],
                                            ['Total', formatMoney(detail.invoice?.total_amount, detailSymbol)],
                                        ].map(([l, v]: any, i: number) => (
                                            <div key={i} className="bg-gray-50 rounded p-3">
                                                <p className="text-[11px] text-gray-500">{l}</p>
                                                <p className="font-semibold text-gray-800">{v}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {detail.zone_totals?.length > 0 && (
                                        <table className="w-full text-sm mb-4">
                                            <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                <tr><th className="text-left py-1">Zone</th><th className="text-left">Shipments</th><th className="text-left">Weight</th><th className="text-right">Amount</th></tr>
                                            </thead>
                                            <tbody>
                                                {detail.zone_totals.map((z: any) => (
                                                    <tr key={z.zone} className="border-t">
                                                        <td className="py-1.5 font-medium">{z.zone}</td>
                                                        <td>{z.shipments}</td>
                                                        <td>{z.weight} kg</td>
                                                        <td className="text-right">{formatMoney(z.amount, detailSymbol)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {/* Docket-level charges as billed. Grouped per charge
                                        rather than per docket: the per-docket rows are in
                                        the CSV export, and an invoice spanning 200 dockets
                                        would bury the shipment lines below. */}
                                    {detail.charge_totals?.length > 0 && (
                                        <>
                                            <p className="text-xs font-semibold text-gray-600 mb-2">Additional charges</p>
                                            <table className="w-full text-sm mb-4">
                                                <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                    <tr>
                                                        <th className="text-left py-1">Charge</th>
                                                        <th className="text-left">Basis</th>
                                                        <th className="text-left">Dockets</th>
                                                        <th className="text-left">Weight</th>
                                                        <th className="text-right">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {detail.charge_totals.map((c: any) => (
                                                        <tr key={c.name} className="border-t">
                                                            <td className="py-1.5 font-medium">{c.name}</td>
                                                            <td className="text-gray-500">
                                                                {formatMoney(c.rate, detailSymbol)}
                                                                {c.type === 'flat' ? ' per docket' : ' per kg'}
                                                            </td>
                                                            <td>{c.dockets}</td>
                                                            <td>{c.type === 'flat' ? '—' : `${c.weight} kg`}</td>
                                                            <td className="text-right">{formatMoney(c.amount, detailSymbol)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}

                                    <p className="text-xs font-semibold text-gray-600 mb-2">Lines ({detail.items?.length || 0})</p>
                                    <div className="overflow-x-auto max-h-[40vh]">
                                        {/* A return invoice is read against the sheet it was built
                                            from, so it shows that sheet's columns rather than zones
                                            and destinations it does not have. */}
                                        {detail.invoice?.bill_type === 'return' ? (
                                            <table className="w-full text-xs">
                                                <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                    <tr>
                                                        <th className="text-left py-1">S/N</th><th className="text-left">Tracking</th>
                                                        <th className="text-left">Weight</th><th className="text-left">Status</th>
                                                        <th className="text-right">Customs duty</th>
                                                        <th className="text-right">Return cost</th><th className="text-right">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(detail.items || []).map((it: any, i: number) => (
                                                        <tr key={it.id} className="border-t">
                                                            <td className="py-1.5">{i + 1}</td>
                                                            <td>{it.shipment_number}</td>
                                                            <td>{it.weight} kg</td>
                                                            <td>{it.shipment_status}</td>
                                                            <td className="text-right">{formatMoney(it.customs_duty, detailSymbol)}</td>
                                                            <td className="text-right">
                                                                {Number(it.base_amount) > 0
                                                                    ? formatMoney(it.base_amount, detailSymbol)
                                                                    : <span className="text-gray-300">—</span>}
                                                            </td>
                                                            <td className="text-right">{formatMoney(it.line_total, detailSymbol)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                        <table className="w-full text-xs">
                                            <thead className="text-[11px] text-[#808080] bg-gray-50 border-b">
                                                <tr>
                                                    <th className="text-left py-1">S/N</th><th className="text-left">Shipment</th>
                                                    <th className="text-left">Date</th><th className="text-left">State</th>
                                                    <th className="text-left">Zone</th><th className="text-left">Weight</th>
                                                    <th className="text-left">Rate applied</th><th className="text-right">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(detail.items || []).map((it: any, i: number) => (
                                                    <tr key={it.id} className="border-t">
                                                        <td className="py-1.5">{i + 1}</td>
                                                        <td>{it.shipment_number}</td>
                                                        <td>{formatDate(it.shipment_created_at)}</td>
                                                        <td>{it.receiver_state}</td>
                                                        <td>{it.zone_name}</td>
                                                        <td>{it.weight} kg</td>
                                                        <td className="text-gray-500">{it.rate_description}</td>
                                                        <td className="text-right">{formatMoney(it.line_total, detailSymbol)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="flex flex-wrap justify-end gap-2 px-5 py-3 border-t">
                            {detail.invoice?.status !== 'void' && (
                                <>
                                    {detail.invoice?.status !== 'sent' && detail.invoice?.status !== 'paid' && (
                                        <button onClick={() => markStatus(detail.invoice, 'sent')} disabled={busy === `status-${detail.invoice?.id}`}
                                            className="bg-blue-600 text-white px-3 py-2 rounded text-sm disabled:opacity-60">Mark sent</button>
                                    )}
                                    {detail.invoice?.status !== 'paid' && (
                                        <button onClick={() => markStatus(detail.invoice, 'paid')} disabled={busy === `status-${detail.invoice?.id}`}
                                            className="bg-green-600 text-white px-3 py-2 rounded text-sm disabled:opacity-60">Mark paid</button>
                                    )}
                                </>
                            )}
                            <button onClick={() => download(detail.invoice, 'xlsx')} className="bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm">Excel</button>
                            <button onClick={() => download(detail.invoice, 'pdf')} className="bg-[#E04343] text-white px-3 py-2 rounded text-sm">PDF</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Name and save the typed weights ─────────────── */}
            {saveSheetOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="px-5 py-3 border-b">
                            <h3 className="font-semibold">Save these weights</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Nothing is invoiced and no shipments are claimed. The weights are kept so you
                                can come back and generate later.
                            </p>
                        </div>
                        <div className="px-5 py-4 space-y-3 text-sm">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                                <input
                                    autoFocus
                                    value={sheetName}
                                    onChange={(e) => setSheetName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveSheet(); }}
                                    placeholder={`${clients.find((c: any) => String(c.id) === String(clientId))?.name || 'Client'} — ${from} to ${to}`}
                                    className="w-full border rounded-md px-3 py-2 text-sm"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Saving under a name you have used before replaces that set.
                                </p>
                            </div>
                            <p className="text-xs text-gray-600">
                                {Object.keys(weightOverrides()).length} docket weight(s) will be saved —
                                only the ones you changed.
                            </p>
                        </div>
                        <div className="px-5 py-3 border-t flex justify-end gap-2">
                            <button onClick={() => setSaveSheetOpen(false)}
                                className="px-3 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300">
                                Cancel
                            </button>
                            <button onClick={saveSheet} disabled={savingSheet}
                                className="px-4 py-2 text-sm rounded-md bg-[#E04343] text-white font-medium disabled:opacity-50">
                                {savingSheet ? 'Saving…' : 'Save weights'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Pick a saved set ────────────────────────────── */}
            {sheetsOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
                        <div className="px-5 py-3 border-b flex items-center justify-between gap-3">
                            <div>
                                <h3 className="font-semibold">Saved weights</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Loading a set drops its weights back into the boxes. Prices are worked out
                                    again from the client's current rate card.
                                </p>
                            </div>
                            <button onClick={() => setSheetsOpen(false)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
                        </div>

                        <div className="px-5 py-2 border-b">
                            <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                                <input type="checkbox" checked={includeUsedSheets}
                                    onChange={(e) => setIncludeUsedSheets(e.target.checked)} />
                                Show sets already used to generate an invoice
                            </label>
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {sheets.length === 0 ? (
                                <p className="px-5 py-8 text-sm text-gray-500 text-center">
                                    Nothing saved for this client yet. Edit some weights and choose
                                    <strong> Save weights</strong>.
                                </p>
                            ) : sheets.map((sh: any) => (
                                <div key={sh.id} className="px-5 py-3 border-b last:border-b-0 flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {sh.name}
                                            {sh.is_used && (
                                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 align-middle">
                                                    used{sh.invoice ? ` · ${sh.invoice.invoice_number}` : ''}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[11px] text-gray-500 mt-0.5">
                                            {sh.dockets} docket{sh.dockets === 1 ? '' : 's'}
                                            {sh.selection?.from ? ` · ${sh.selection.from} to ${sh.selection.to}` : ''}
                                            {sh.updated_by ? ` · ${sh.updated_by}` : ''}
                                            {sh.updated_at ? ` · ${sh.updated_at}` : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button onClick={() => applySheet(sh)}
                                            className="text-xs rounded bg-[#E04343] text-white px-3 py-1.5">
                                            Load
                                        </button>
                                        <button onClick={() => removeSheet(sh)}
                                            className="text-xs rounded border border-gray-300 px-2 py-1.5 text-gray-600 hover:bg-gray-50">
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={5000} newestOnTop closeOnClick pauseOnHover />
        </>
    );
}

export default ClientInvoices;
